/**
 * 放送地点に紐づくコンテンツ (`BroadcastContent`) のカテゴリ定義と
 * 正規化／初期値ヘルパーをまとめたモジュール。
 *
 * - カテゴリ毎の表示名・必要プロパティ・デフォルト値を 1 箇所に集約しておくことで、
 *   フォーム / バリデーション / Import 時の型変換すべてで同じメタデータを再利用できる。
 * - `BroadcastPoint` のレガシーフィールド (`delay_threshold_seconds` トップレベル)
 *   を読み込んだ場合は、自動的に「自列車遅延」コンテンツに変換する。
 */
import type {
  ArrivalTrackCondition,
  BroadcastCondition,
  BroadcastContent,
  BroadcastContentCategory,
  BroadcastPoint,
  ConnectingDelayJudgementMode,
  ConnectingDirectionEntry,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayCondition,
  ConnectingLineDelayProperties,
  ConnectingLineEntry,
  ConnectingStationOverLimitBehavior,
  DelayBroadcastGranularity,
  DelayBroadcastPattern,
  FreeTextProperties,
  SelfTrainDelayProperties,
  TrainInfoLineEntry,
  TrainInfoProperties,
} from '../api/types';
/*
 * 言語マスタ (`data/pollyVoices.ts`) は Python 正源
 * (`lambda/common/polly_voices.py`) から機械生成された **依存を持たない**
 * 定義ファイルなので、`utils` から参照しても循環参照にならない
 * (逆に `data` → `utils` を作ると循環するので注意)。
 */
import { DEFAULT_LANGUAGE_KEY, findPollyLanguage } from '../data/pollyVoices';
import type { LocalizedText } from './localizedSsml';
import {
  isWellFormedSsml,
  localizedTextFieldTestId,
  unbakeLocalizedSsmlDefaults,
} from './localizedSsml';
import type { ValidationIssue, ValidationTarget } from './validationIssues';

import { firstIssueMessage } from './validationIssues';

/**
 * `BroadcastContent.train_type` (列車種別) として許容される値の一覧。
 *
 * - `'all'` = 全列車 (既定値) / `'rapid'` = 快速 / `'local'` = 各駅停車。
 * - サーバ側 `ALLOWED_TRAIN_TYPES` (`lambda/control-panel-api/index.py`) と
 *   必ず一致させること。サーバはこの 3 値以外を 400 にする。
 * - UI 側の表示ラベルは `components/broadcastContents/constants.ts` の
 *   `TRAIN_TYPE_META` が持つ。**本ファイル (utils) から components を import すると
 *   依存方向が逆転する** ため、値の定義のみここに独立して持つ
 *   (両者がズレないよう `broadcastContents.test.ts` で突き合わせる)。
 */
const ALLOWED_TRAIN_TYPES: ReadonlyArray<NonNullable<BroadcastContent['train_type']>> = [
  'all',
  'rapid',
  'local',
];

/** `train_type` 未設定時のデフォルト値 (全列車)。この値はキーごと保存しない。 */
const TRAIN_TYPE_DEFAULT = 'all';

/**
 * `BroadcastContent.train_numbers` (放送対象の列車番号 / Issue #530) の上限件数。
 *
 * サーバ側 `MAX_TRAIN_NUMBERS` (`lambda/control-panel-api/index.py`) と必ず
 * 一致させること。サーバはこれを超える件数を 400 にする。
 */
export const MAX_TRAIN_NUMBERS = 100;

/**
 * 列番 1 件あたりの最大文字数。
 *
 * サーバ側 `MAX_TRAIN_NUMBER_LENGTH` (`lambda/control-panel-api/index.py`) と
 * 必ず一致させること。ATOS 列番は「数字 4 桁 + 英字 1〜2 文字」(例 `1001A`) 程度で、
 * 実運用では 10 文字を超えない。
 */
export const MAX_TRAIN_NUMBER_LENGTH = 16;

/**
 * 列番として許容する文字種 (半角英数字 + ハイフン)。
 *
 * サーバ側 `_TRAIN_NUMBER_PATTERN` (`lambda/control-panel-api/index.py`) と
 * 同じ正規表現。全角文字・空白・記号を弾くことで「見た目は同じなのに照合できない」
 * 設定ミスを保存前に検知できる。
 */
export const TRAIN_NUMBER_PATTERN = /^[0-9A-Za-z-]+$/;

export interface CategoryMeta {
  category: BroadcastContentCategory;

  /** 画面表示用ラベル */
  label: string;
  /** 短い説明 (フォーム説明文用) */
  description: string;
  /** カテゴリ既定値の properties オブジェクトを返す */
  defaultProperties: () => BroadcastContent['properties'];
}

/**
 * 各放送粒度に対応するデフォルト SSML テンプレート文言 (自列車遅延用)。
 * UI の「放送パターン」欄の placeholder / 表示に使用する。
 *
 * 自列車遅延では「ご乗車の電車は」を主語とする従来文面を維持する
 * (運用上、自列車側で線名を読み上げる必要性が低いため)。
 * カスタムテンプレートで自路線の一般的な線名を読みたい場合は ``{route_name}``
 * プレースホルダを使えるよう Lambda 側で展開対応している (route_name 対応)。
 *
 * 既存ユーザーの設定値は ``properties.delay_patterns[].ssml_template`` に個別保存
 * されており、本デフォルトは「未指定時の placeholder / フォールバック」にのみ使われる
 * ため、後方互換は壊れない。
 */
export const GRANULARITY_DEFAULT_SSML: Record<DelayBroadcastGranularity, string> = {
  '1分単位': 'ご乗車の電車は現在、{delay_minutes_1}分遅れで走行しています。',
  '5分単位': 'ご乗車の電車は現在、約{delay_minutes_5}分遅れで走行しています。',
  '10分単位': 'ご乗車の電車は現在、約{delay_minutes_10}分遅れで走行しています。',
  大幅な遅延: 'ご乗車の電車は現在、大幅な遅れで走行しています。',
};

/**
 * 接続線遅延 (transfer_aware モード) 用のデフォルト SSML テンプレート文言。
 * 表形式 UI の「放送パターン」欄 placeholder に表示し、Lambda 側の
 * ``CONNECTING_GRANULARITY_DEFAULT_TEMPLATE`` (放送時の既定文面) と一致させる。
 *
 * - 文面前半は「{from_prefix}お乗換の{route_name}{direction_name}は、」で固定。
 *   ``{from_prefix}`` は Lambda 側で ``from_station`` (= 乗換駅) があれば
 *   「<駅名>駅から」、無ければ空文字に出し分けされる派生プレースホルダ。
 *   transfer_aware モードでは ``transfer_stop_id`` が必須のため、実放送では
 *   常に「<駅名>駅から」となる (例: 「新宿駅からお乗換の…」)。
 *   ``{route_name}`` (一般線名) / ``{direction_name}`` (案内文面用方面文言・
 *   「方面行き」結合済み) も Lambda 側で展開される。
 * - 後半の粒度別分数表現:
 *   - 1分単位: 「現在 {delay_minutes_1} 分遅れています」(切り捨て分数のため「ほど」は付けない)
 *   - 5分単位 / 10分単位: 「現在 {delay_minutes_N} 分ほど遅れています」(切り捨てでざっくり読み上げ)
 *   - 大幅な遅延: 「現在大幅な遅れがでています」(分数を読み上げない)
 *
 * Lambda 側 (``lambda/update-contents/index.py`` の
 * ``CONNECTING_GRANULARITY_DEFAULT_TEMPLATE``) と文字列レベルで一致させているため、
 * デフォルト文面の挙動を変更する際は両方を同時に更新する必要がある。
 */
export const CONNECTING_GRANULARITY_DEFAULT_SSML: Record<DelayBroadcastGranularity, string> = {
  '1分単位':
    '{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_1}分遅れています。',
  '5分単位':
    '{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_5}分ほど遅れています。',
  '10分単位':
    '{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_10}分ほど遅れています。',
  大幅な遅延: '{from_prefix}お乗換の{route_name}{direction_name}は、現在大幅な遅れがでています。',
};

/** 放送粒度の選択肢一覧 */
export const DELAY_BROADCAST_GRANULARITIES: readonly DelayBroadcastGranularity[] = [
  '1分単位',
  '5分単位',
  '10分単位',
  '大幅な遅延',
];

/**
 * 自列車遅延コンテンツのデフォルト遅延放送パターン (新規作成時の初期値)。
 * Q1 回答: 1分以上〜10分未満 / 10分以上〜30分未満 / 30分以上〜60分未満 / 60分以上
 *
 * Issue #527: 各行の ``ssml_template`` は **入れない (空欄 = 定型文マスタへ委譲)**。
 * 実値として焼き込むと個別文面がマスタより優先されてしまい、
 * 「定型文マスタを編集したら既存コンテンツの放送内容も一括で変わる」という
 * 受け入れ条件が成立しなくなるため (詳細は `backfillDefaultSsmlTemplates` の説明参照)。
 * 未入力時に実際に読み上げられる文面は入力欄の placeholder
 * (`GRANULARITY_DEFAULT_SSML`) で提示する。
 */
const DEFAULT_DELAY_PATTERNS: readonly DelayBroadcastPattern[] = [
  { delay_minutes_from: 1, delay_minutes_to: 10, granularity: '1分単位' },
  { delay_minutes_from: 10, delay_minutes_to: 30, granularity: '5分単位' },
  { delay_minutes_from: 30, delay_minutes_to: 60, granularity: '10分単位' },
  { delay_minutes_from: 60, granularity: '大幅な遅延' },
];

/**
 * 接続線遅延コンテンツ (transfer_aware モード) のデフォルト遅延放送パターン。
 *
 * 自列車遅延と同じ「遅れ分数 → 放送粒度」の対応で初期値を構成する。
 * 判定モードを `transfer_aware` に切り替えたタイミングで `delay_patterns` 未設定なら
 * このパターンを初期値として注入する。
 *
 * Issue #527: 各行の ``ssml_template`` は **入れない (空欄 = 定型文マスタへ委譲)**。
 * 理由は `DEFAULT_DELAY_PATTERNS` と同じ (マスタ編集で一括反映させるため)。
 */
export const DEFAULT_CONNECTING_DELAY_PATTERNS: readonly DelayBroadcastPattern[] = [
  { delay_minutes_from: 1, delay_minutes_to: 10, granularity: '1分単位' },
  { delay_minutes_from: 10, delay_minutes_to: 30, granularity: '5分単位' },
  { delay_minutes_from: 30, delay_minutes_to: 60, granularity: '10分単位' },
  { delay_minutes_from: 60, granularity: '大幅な遅延' },
];

/**
 * 接続線遅延コンテンツ (whole_line / section_scheduled モード) の
 * 単一 SSML テンプレートのデフォルト文言。
 *
 * これまで <textarea> の placeholder で表示していた文言を、新規作成時の初期値として
 * 実際に `ssml_template` に書き込む。`whole_line` モードでは Lambda 側で
 * `{from_prefix}` / `{from_station}` / `{direction_name}` が空文字に強制展開されるため、
 * これらを含まない短い文面を採用する。
 */
export const CONNECTING_SINGLE_SSML_DEFAULT: Record<'whole_line' | 'section_scheduled', string> = {
  whole_line: '<speak>お乗換の{route_name}は、現在遅れて運行しています</speak>',
  section_scheduled:
    '<speak>{from_prefix}お乗換の{route_name}{direction_name}は、現在{delay_minutes_1}分ほど遅れて運行しています。</speak>',
};

// ---------------------------------------------------------------------------
// 接続線遅延 (乗換駅ベース / part2 / `connecting_line_delay_by_station`) の
// デフォルト SSML テンプレート・デフォルトパターン。
// ---------------------------------------------------------------------------

/**
 * 接続線遅延 (乗換駅ベース / part2) の **冒頭 SSML テンプレート** のデフォルト文言。
 *
 * ``{transfer_station}`` プレースホルダは Lambda 側で乗換駅名に展開される
 * (例: 「赤羽」)。UI の <input> 入力欄の placeholder / 新規作成時の初期値としても使う。
 *
 * Lambda 側 (``lambda/update-contents/index.py`` の
 * ``DEFAULT_CONNECTING_STATION_INTRO_TEMPLATE``) と文字列レベルで一致させる。
 */
export const CONNECTING_STATION_INTRO_DEFAULT_SSML = '{transfer_station}駅からお乗換の';

/**
 * 接続線遅延 (乗換駅ベース / part2) の **上限超過時の案内文** のデフォルト文言 (ja)。
 *
 * 入力欄の placeholder (薄字) の最終フォールバックとしてのみ使う。値として保存しない。
 * Lambda 側 (``lambda/common/ssml_builder.py`` の
 * ``DEFAULT_CONNECTING_STATION_OVER_LIMIT_TEMPLATE``) と文字列レベルで一致させる。
 * 外国語は `LOCALIZED_STATION_OVER_LIMIT` (`utils/localizedSsml.ts`)。
 */
export const CONNECTING_STATION_OVER_LIMIT_DEFAULT_SSML =
  '{transfer_station}駅付近走行の各路線に遅れが出ております。駅の案内放送、電光掲示板にご注意ください';

/**
 * 接続線遅延 (乗換駅ベース / part2) の **接続線ブロック SSML テンプレート** のデフォルト文言。
 *
 * ``{route_name}`` は路線の一般名称に、``{directions_text}`` は方面部分文
 * (part2 の各 ``ConnectingDirectionEntry`` を read out するために ``delay_patterns[i].ssml_template``
 * から組み立てた文字列) を読点連結した結果に、Lambda 側で展開される。
 *
 * 「〜は、」の「は」を含めていないのは、方面部分文の冒頭で「〜方面は」と表現するため、
 * 二重「は」を回避する意図。
 *
 * Lambda 側 (``DEFAULT_CONNECTING_STATION_LINE_TEMPLATE``) と一致させる。
 */
export const CONNECTING_STATION_LINE_DEFAULT_SSML =
  '{route_name}、{directions_text}となっております。';

/**
 * 接続線遅延 (乗換駅ベース / part2) の **方面部分文 SSML テンプレート** のデフォルト文言。
 *
 * 表形式 UI の各行 (=遅延放送パターン) にひも付き、Lambda 側でこの文言を各方面ぶん
 * 展開してから読点で連結し、``{directions_text}`` プレースホルダに埋め込む。
 *
 * 使えるプレースホルダ:
 * - ``{direction_name}`` (方面名、必須。空だと放送文が破綻するのでバリデーションで担保)
 * - ``{delay_minutes_1/5/10}`` / ``{delay_minutes}`` / ``{delay_seconds}`` / ``{delay}``
 * - ``{route_name}`` / ``{route_id}`` / ``{route}``
 * - ``{transfer_station}``
 *
 * Lambda 側 (``CONNECTING_STATION_GRANULARITY_DEFAULT_TEMPLATE``) と一致させる。
 */
export const CONNECTING_STATION_GRANULARITY_DEFAULT_SSML: Record<
  DelayBroadcastGranularity,
  string
> = {
  '1分単位': '{direction_name}方面は{delay_minutes_1}分遅れ',
  '5分単位': '{direction_name}方面は約{delay_minutes_5}分遅れ',
  '10分単位': '{direction_name}方面は約{delay_minutes_10}分遅れ',
  大幅な遅延: '{direction_name}方面は大幅な遅れ',
};

/**
 * 接続線遅延 (乗換駅ベース / part2) の **方面部分文 SSML テンプレート** のデフォルト文言
 * (判定モード `whole_line` 用)。
 *
 * `whole_line` (接続線全体の遅延) は接続線全体の最大遅延だけで判定する純化モードなので、
 * コンパネ上でも「方面」の入力を求めない (方面欄そのものを表示しない)。
 * その状態で `CONNECTING_STATION_GRANULARITY_DEFAULT_SSML` (`{direction_name}` 入り) を
 * 使うと「埼京線、方面は5分遅れとなっております。」のように主語が欠けた文面になるため、
 * **方面に触れない文面** を専用のデフォルトとして持つ。
 *
 * 例) 接続線ブロック文と合成すると「埼京線、約5分遅れとなっております。」
 *
 * Lambda 側 (`CONNECTING_STATION_WHOLE_LINE_GRANULARITY_DEFAULT_TEMPLATE`) と一致させる。
 */
export const CONNECTING_STATION_WHOLE_LINE_GRANULARITY_DEFAULT_SSML: Record<
  DelayBroadcastGranularity,
  string
> = {
  '1分単位': '{delay_minutes_1}分遅れ',
  '5分単位': '約{delay_minutes_5}分遅れ',
  '10分単位': '約{delay_minutes_10}分遅れ',
  大幅な遅延: '大幅な遅れ',
};

// ---------------------------------------------------------------------------
// 接続線の発車見込み案内 (`departure_forecast`) のデフォルト文言・デフォルトパターン (SBI-5)。
// ---------------------------------------------------------------------------

/**
 * 発車見込み案内 (`departure_forecast`) の遅延放送パターンに **固定で保存する放送粒度**。
 *
 * このモードは遅延分数を読み上げないため、粒度は文面に影響しない
 * (バックエンド `build_connecting_departure_forecast_ssml` は粒度を参照しない)。
 * 一方 `DelayBroadcastPattern.granularity` は必須項目なので、画面では粒度の列を隠し、
 * 常にこの値を保存する (既存の `createDefaultPattern` の既定粒度とも一致)。
 */
export const DEPARTURE_FORECAST_FIXED_GRANULARITY: DelayBroadcastGranularity = '1分単位';

/**
 * 接続線の発車見込み案内 (part1) の **完結文** のデフォルト文言 (日本語)。
 *
 * Lambda 側 `DEFAULT_DEPARTURE_FORECAST_TEMPLATE` (`lambda/common/ssml_builder.py`) と
 * 文字列レベルで一致させること。定型文マスタ ID は
 * `connecting_delay_departure_forecast` (粒度別ではなく単一)。
 *
 * `{headsign}` は接続便の行先 (取得できないときは `direction_name`)、
 * `{departure_time}` は発車見込み時刻 (日本語は「12時13分」)。
 */
export const CONNECTING_DEPARTURE_FORECAST_DEFAULT_SSML =
  '{from_prefix}お乗換の{route_name}{headsign}は{departure_time}頃、発車の見込みです。';

/**
 * 接続線の発車見込み案内 (part2) の **接続線ブロック文** のデフォルト文言 (日本語)。
 *
 * 方面の部分文は語尾を持たないため、この文が「発車の見込みです」で閉じる。
 * Lambda 側 `DEFAULT_CONNECTING_STATION_DEPARTURE_FORECAST_LINE_TEMPLATE` と一致させること。
 * 定型文マスタ ID は `connecting_station_departure_forecast_line`。
 */
export const CONNECTING_STATION_DEPARTURE_FORECAST_LINE_DEFAULT_SSML =
  '{route_name}、{directions_text}、発車の見込みです。';

/**
 * 接続線の発車見込み案内 (part2) の **方面部分文** のデフォルト文言 (日本語)。
 *
 * 「{headsign}は{departure_time}頃」まで。語尾は持たない (line 文が閉じる)。
 * Lambda 側 `DEFAULT_CONNECTING_STATION_DEPARTURE_FORECAST_TEMPLATE` と一致させること。
 * 定型文マスタ ID は `connecting_station_departure_forecast` (粒度別ではなく単一)。
 */
export const CONNECTING_STATION_DEPARTURE_FORECAST_DEFAULT_SSML = '{headsign}は{departure_time}頃';

/**
 * 全粒度で同じ文面を返す `Record<粒度, 文面>` を作る。
 *
 * 発車見込み案内は粒度別のテンプレートを持たず単一の文面だが、`DelayPatternRow` の
 * `placeholderTemplates` は粒度をキーにした辞書を受けるため、その形に揃える。
 */
export function uniformGranularityTemplates(
  text: string,
): Record<DelayBroadcastGranularity, string> {
  return {
    '1分単位': text,
    '5分単位': text,
    '10分単位': text,
    大幅な遅延: text,
  };
}

/** part1 発車見込み案内の行 placeholder (全粒度で同じ完結文)。 */
export const CONNECTING_DEPARTURE_FORECAST_PATTERN_DEFAULT_SSML: Record<
  DelayBroadcastGranularity,
  string
> = uniformGranularityTemplates(CONNECTING_DEPARTURE_FORECAST_DEFAULT_SSML);

/** part2 発車見込み案内の行 placeholder (全粒度で同じ方面部分文)。 */
export const CONNECTING_STATION_DEPARTURE_FORECAST_PATTERN_DEFAULT_SSML: Record<
  DelayBroadcastGranularity,
  string
> = uniformGranularityTemplates(CONNECTING_STATION_DEPARTURE_FORECAST_DEFAULT_SSML);

/**
 * 発車見込み案内 (part1 / part2 共通) のデフォルト遅延パターン。
 *
 * 発車見込み案内では `delay_patterns` を「どの遅れ分数の範囲で案内するか」にだけ使う
 * (該当行が無ければ放送しない)。遅れ 1 分以上で案内する 1 行を初期値とする
 * (定時 = 0 分も案内したい場合は、運用者が「0 分以上」の行にする)。
 *
 * Issue #527: ``ssml_template`` は入れない (空欄 = 定型文マスタへ委譲)。
 */
export const DEFAULT_DEPARTURE_FORECAST_PATTERNS: readonly DelayBroadcastPattern[] = [
  { delay_minutes_from: 1, granularity: DEPARTURE_FORECAST_FIXED_GRANULARITY },
];

/**
 * 判定モードが発車見込み案内 (`departure_forecast`) か。
 *
 * 「このモードだけ粒度の列を隠す」「単一テンプレートを使わない」などの分岐を
 * 文字列比較の散在ではなく 1 箇所に集約する。
 */
export function isDepartureForecastMode(mode: ConnectingDelayJudgementMode | undefined): boolean {
  return mode === 'departure_forecast';
}

/**
 * 判定モードが「遅れ分数の範囲 × 放送パターン」の表 (`delay_patterns`) で設定するモードか。
 *
 * `transfer_aware` と `departure_forecast` が該当する (どちらも接続便を特定して判定する)。
 */
export function usesDelayPatternsTable(mode: ConnectingDelayJudgementMode | undefined): boolean {
  return mode === 'transfer_aware' || mode === 'departure_forecast';
}

/**
 * 判定モードの切替が「発車見込み案内 ⇔ それ以外」をまたぐか。
 *
 * 発車見込み案内の遅延パターンは「案内する範囲」、他モードは「遅延の放送粒度」と
 * 意味が違い、行の文面 (`ssml_template`) も互換性が無い
 * (遅延文に `{headsign}` / `{departure_time}` は無く、その逆も同様)。
 * そのためまたぐときは遅延パターンを新モードの既定へ戻す。
 */
export function crossesDepartureForecastBoundary(
  prevMode: ConnectingDelayJudgementMode | undefined,
  nextMode: ConnectingDelayJudgementMode,
): boolean {
  return isDepartureForecastMode(prevMode) !== isDepartureForecastMode(nextMode);
}

/**
 * 発車見込み案内の遅延パターンを保存用に正規化する (粒度を固定値に揃える)。
 *
 * 画面では粒度を選べないので通常は既に固定値だが、JSON インポートや他モードからの
 * 切替で別の粒度が残っていても、保存時には必ず固定値へ揃える。
 */
function pinDepartureForecastGranularity(pattern: DelayBroadcastPattern): DelayBroadcastPattern {
  return pattern.granularity === DEPARTURE_FORECAST_FIXED_GRANULARITY
    ? pattern
    : { ...pattern, granularity: DEPARTURE_FORECAST_FIXED_GRANULARITY };
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の判定モードに対応する
 * 「方面部分文 SSML テンプレートのデフォルト文言セット」を返す。
 *
 * - `whole_line`: 方面を扱わないので `{direction_name}` を含まない文面
 * - `departure_forecast`: 遅延分数ではなく発車見込み時刻を案内する単一文面
 *   (`{headsign}は{departure_time}頃`。粒度によらず同じ)
 * - それ以外 (`section_scheduled` / `transfer_aware`): 従来の方面入り文面
 *
 * デフォルト文の適用 (新規作成 / 粒度変更 / 判定モード切替 / backfill) は
 * すべてこのヘルパ経由にして、モードとデフォルト文の対応を 1 箇所に閉じ込める。
 */
export function getConnectingStationGranularityDefaults(
  mode: ConnectingDelayJudgementMode | undefined,
): Record<DelayBroadcastGranularity, string> {
  if (mode === 'departure_forecast')
    return CONNECTING_STATION_DEPARTURE_FORECAST_PATTERN_DEFAULT_SSML;
  return mode === 'whole_line'
    ? CONNECTING_STATION_WHOLE_LINE_GRANULARITY_DEFAULT_SSML
    : CONNECTING_STATION_GRANULARITY_DEFAULT_SSML;
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の判定モードが「方面の入力を必要とするか」を返す。
 *
 * `whole_line` (接続線全体の遅延) は接続線全体の最大遅延だけで判定し、放送文でも
 * 方面名を読み上げないため **方面の入力は不要** (UI 上も方面セクションを表示せず、
 * 必須バリデーションも免除する)。
 *
 * 判定を 1 箇所に集約し、UI / バリデーション / プレビュー条件で同じ規則を使う。
 */
export function requiresConnectingStationDirections(
  mode: ConnectingDelayJudgementMode | undefined,
): boolean {
  return mode !== 'whole_line';
}

/**
 * 方面部分文 SSML が「いずれかの判定モードのデフォルト文そのまま (= ユーザー未編集)」か判定する。
 *
 * 判定モード切替時にデフォルト文を差し替えてよいかを決めるために使う。
 * ユーザーが自分で書き換えた文面を勝手に上書きしないよう、
 * **全モード・全粒度のデフォルト文と突き合わせて一致した場合のみ** 「未編集」とみなす。
 */
function isUntouchedConnectingStationDirectionSsml(template: string | undefined): boolean {
  if (typeof template !== 'string') return true;
  const trimmed = template.trim();
  if (trimmed === '') return true;
  const defaults = [
    ...Object.values(CONNECTING_STATION_GRANULARITY_DEFAULT_SSML),
    ...Object.values(CONNECTING_STATION_WHOLE_LINE_GRANULARITY_DEFAULT_SSML),
    CONNECTING_STATION_DEPARTURE_FORECAST_DEFAULT_SSML,
  ];
  return defaults.includes(trimmed);
}

/**
 * 接続線ブロック文 (`ssml_line_template`) が「いずれかのモードのデフォルト文そのまま
 * (= ユーザー未編集)」か判定する。発車見込み案内をまたぐ切替で文面を空欄へ戻してよいかを
 * 決めるために使う。
 */
function isUntouchedConnectingStationLineSsml(template: string | undefined): boolean {
  if (typeof template !== 'string') return true;
  const trimmed = template.trim();
  return (
    trimmed === '' ||
    trimmed === CONNECTING_STATION_LINE_DEFAULT_SSML ||
    trimmed === CONNECTING_STATION_DEPARTURE_FORECAST_LINE_DEFAULT_SSML
  );
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の接続線ブロックに「判定モード切替」を適用する。
 *
 * 判定モードによって使う入力項目・放送文面が変わるため、切替時に
 * **そのモードで使わない値を落とし、そのモード向けのデフォルト文面へ寄せる**。
 * UI (`ConnectingLineBlock`) から呼ぶ純関数として切り出し、変換規則を 1 箇所に集約する。
 *
 * 適用内容:
 * 1. `delay_judgement_mode` を `nextMode` に更新する。
 * 2. `section_scheduled` 以外では方面ごとの区間駅 (`start_stop_id` / `end_stop_id`) を落とす
 *    (別モードの入力が DB / JSON にゴミとして残らないようにする既存方針を踏襲)。
 * 3. `whole_line` では **方面の入力自体が不要** なので、方面を 1 件に畳んで
 *    判定用 `direction_id` / 案内文面用 `direction_name` を空にする。
 *    (方面は放送文・遅延判定の繰り返し単位なので、配列自体は 1 件残す)
 * 4. 方面部分文 SSML (`delay_patterns[].ssml_template`) が **未編集のデフォルト文** なら、
 *    空欄 (`undefined`) に戻して定型文マスタへ委譲する (Issue #527 / #627)。
 *    モード別の文面はマスタ側 (`connecting_station_whole_line_*` 等) が解決するため、
 *    日本語の既定文を実値で保存して外国語放送に漏らさない。
 *    ユーザーが手で書き換えた文面はそのまま維持する。
 * 5. 発車見込み案内 (`departure_forecast`) をまたぐ切替では、遅延パターンを新モードの
 *    既定へ **置き換える** (意味が違う: 「放送粒度」⇔「案内する遅れ分数の範囲」。
 *    行数も違うので、遅れ分数の範囲は引き継がない)。
 *    - `departure_forecast` へ: 「1 分以上」の 1 行 (`DEFAULT_DEPARTURE_FORECAST_PATTERNS`)。
 *    - `departure_forecast` から: 他モードの既定行 (`DEFAULT_CONNECTING_STATION_DELAY_PATTERNS`)。
 *    行ごとの個別文面 (`ssml_template` / `ssml_template_i18n`) も、文面に互換性が無いので捨てる。
 *    あわせて、このモードでは使わない接続線ブロック文 (`ssml_line_template`) の
 *    デフォルト文も未編集なら空欄へ戻す (専用のマスタ ID に切り替わるため)。
 */
export function applyConnectingStationJudgementMode(
  line: ConnectingLineEntry,
  nextMode: ConnectingDelayJudgementMode,
): ConnectingLineEntry {
  const currentDirections = Array.isArray(line.directions) ? line.directions : [];
  // 2 + 3: 方面の値をモードに合わせて整理する。
  let nextDirections: ConnectingDirectionEntry[];
  if (nextMode === 'whole_line') {
    // 方面を使わないモード。1 件だけ残して中身を空にする (放送の繰り返し単位は維持)。
    nextDirections = [createDefaultConnectingStationDirection()];
  } else if (nextMode === 'section_scheduled') {
    // 区間駅を使うモード。既存の値をそのまま引き継ぐ。
    nextDirections =
      currentDirections.length > 0
        ? currentDirections
        : [createDefaultConnectingStationDirection()];
  } else {
    // transfer_aware / departure_forecast: 区間駅は使わないので落とす。
    const base =
      currentDirections.length > 0
        ? currentDirections
        : [createDefaultConnectingStationDirection()];
    nextDirections = base.map((d) => ({
      ...d,
      start_stop_id: undefined,
      end_stop_id: undefined,
    }));
    // NOTE: `route_directions` (線区ごとの判定方面) は transfer_aware /
    //       section_scheduled のどちらでも判定に使うため、ここでは落とさない。
    //       落とすのは方面を一切使わない `whole_line` のみ (上の分岐で
    //       方面ごと作り直すため、結果的にクリアされる)。
  }

  // 4: 未編集のデフォルト文は実値で焼き込まず空欄 (= 定型文マスタへ委譲) に戻す。
  //    日本語の既定文を `ssml_template` に保存すると、`ssml_template_i18n` の無い
  //    外国語放送でも日本語がそのまま読み上げられてしまうため (Issue #627)。
  const crosses = crossesDepartureForecastBoundary(line.delay_judgement_mode, nextMode);
  let nextPatterns: DelayBroadcastPattern[] | undefined;
  if (crosses) {
    // 行の意味・行数・文面がモード間で互換しないため、範囲も個別文面も引き継がず既定へ置き換える。
    nextPatterns = (
      isDepartureForecastMode(nextMode)
        ? DEFAULT_DEPARTURE_FORECAST_PATTERNS
        : DEFAULT_CONNECTING_STATION_DELAY_PATTERNS
    ).map((p) => ({ ...p }));
  } else if (Array.isArray(line.delay_patterns)) {
    nextPatterns = line.delay_patterns.map((p) => {
      if (!isUntouchedConnectingStationDirectionSsml(p.ssml_template)) return p;
      return { ...p, ssml_template: undefined };
    });
  } else {
    nextPatterns = line.delay_patterns;
  }

  const next: ConnectingLineEntry = {
    ...line,
    delay_judgement_mode: nextMode,
    directions: nextDirections,
    delay_patterns: nextPatterns,
  };
  // 接続線ブロック文: 発車見込み案内をまたぐ切替で未編集の既定文なら、空欄
  // (= モード専用のマスタ ID へ委譲) に戻す。手で書いた文面は維持する。
  if (
    crosses &&
    next.ssml_line_template !== undefined &&
    isUntouchedConnectingStationLineSsml(next.ssml_line_template)
  ) {
    delete next.ssml_line_template;
  }
  return next;
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ぶんのデフォルト遅延放送パターン。
 *
 * part1 の ``DEFAULT_CONNECTING_DELAY_PATTERNS`` と同じ「遅れ分数 → 放送粒度」の対応で
 * 初期値を構成する。接続線ブロックを新規追加した際にこのパターンを注入する。
 *
 * 各行の ``ssml_template`` は **方面部分文** (`{directions_text}` に流し込まれる素材) を
 * 表す点が part1 と違うので、専用のデフォルト文言を持つ (`CONNECTING_STATION_GRANULARITY_DEFAULT_SSML`)。
 */
export const DEFAULT_CONNECTING_STATION_DELAY_PATTERNS: readonly DelayBroadcastPattern[] = [
  // Issue #527: ssml_template は入れない (空欄 = 定型文マスタへ委譲)。
  { delay_minutes_from: 1, delay_minutes_to: 10, granularity: '1分単位' },
  { delay_minutes_from: 10, delay_minutes_to: 30, granularity: '5分単位' },
  { delay_minutes_from: 30, delay_minutes_to: 60, granularity: '10分単位' },
  { delay_minutes_from: 60, granularity: '大幅な遅延' },
];

/**
 * 放送粒度に対応するデフォルト方面部分文 SSML を返すヘルパー (接続線遅延 part2 用)。
 * 粒度プルダウンを変更したタイミングで、対応する粒度のデフォルト文に
 * ssml_template を上書きする目的で使用する (自列車遅延 / part1 と同様の運用)。
 */
export function getDefaultConnectingStationSsml(granularity: DelayBroadcastGranularity): string {
  return CONNECTING_STATION_GRANULARITY_DEFAULT_SSML[granularity];
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の「新規接続線ブロック」既定値を生成する。
 * ＋ボタンで接続線を追加したときに使う。
 */
export function createDefaultConnectingStationLine(): ConnectingLineEntry {
  return {
    route_id: '',
    // 既定の判定モードは「乗り換え列車の遅延」(transfer_aware)。プルダウン先頭の選択肢と揃える。
    delay_judgement_mode: 'transfer_aware',
    transfer_time_seconds: 180,
    // Issue #527: ssml_line_template は入れない (空欄 = 定型文マスタ
    // `connecting_station_line` へ委譲)。
    delay_patterns: DEFAULT_CONNECTING_STATION_DELAY_PATTERNS.map((p) => ({ ...p })),
    directions: [createDefaultConnectingStationDirection()],
  };
}

/**
 * 1 接続線エントリあたりの「遅延検索対象 route_id」(`route_ids`) の上限件数。
 *
 * サーバ側 `MAX_CONNECTING_ROUTE_IDS` (`lambda/control-panel-api/index.py`) と
 * **必ず一致させること**。放送側 (UpdateContents) は route_id ごとに遅延を取得
 * するため、件数がそのまま外部 API 呼び出し数の上限になる。
 *
 * 超過は UI 側 (追加プルダウンの disabled + 保存時バリデーション) と
 * サーバ側 (400) の二重で防ぐ。
 */
export const MAX_CONNECTING_ROUTE_IDS = 10;

/**
 * 任意テキスト (free_text) の「放送文面 (通常テキスト)」の最大文字数。
 *
 * サーバ側 `MAX_FREE_TEXT_LENGTH` (`lambda/control-panel-api/index.py`) と **必ず一致させること**。
 * 言語別文面 (`text_i18n`) の `MAX_I18N_TEXT_LENGTH` と同じ値で、根拠は Polly の入力上限
 * (SSML 6000 文字 / 課金文字 3000)。超えると当該言語が丸ごと無音になるため、
 * 保存前 (画面) とサーバ側 (400) の二重で防ぐ。
 */
export const MAX_FREE_TEXT_LENGTH = 3000;

/**
 * 言語別文面 (`text_i18n` / `ssml_i18n`) 1 件あたりの最大文字数。
 *
 * サーバ側 `MAX_I18N_TEXT_LENGTH` (`lambda/control-panel-api/index.py`) と **必ず一致させること**。
 * 日本語の通常テキスト ({@link MAX_FREE_TEXT_LENGTH}) と同じ値。
 * 画面で先に弾かないと、サーバの英語メッセージ (`...must be 3000 characters or fewer`) が
 * そのままフッターに出て、日本語の超過時と見え方が食い違う。
 */
export const MAX_I18N_TEXT_LENGTH = 3000;

/**
 * 文面の文字数を数える (サーバの `len(text.strip())` と同じ規則)。
 *
 * 前後の空白を除き、コードポイント単位で数える。`String.length` は UTF-16 単位で
 * 結合文字・絵文字が 2 と数えられ、サーバと食い違うため使わない。
 */
function countTextCharacters(text: string): number {
  return Array.from(text.trim()).length;
}

/**
 * 接続線ブロックの「遅延検索対象の線区」を解決する (放送側と同じ規則)。
 *
 * 検索対象は **`route_id` + `route_ids`** で、`route_id` は常に先頭に入る。
 * 順序を保ったまま重複を排除する (`route_ids` に `route_id` と同じ線区が入っている
 * 既存データでも二重に数えない)。
 *
 * 放送側 `_extract_connecting_route_ids`
 * (`lambda/update-contents/connecting_station.py`) と **必ず一致させること**。
 * 画面の「検索対象の合計表示」や「遅延を判定する向き」表の行が、実際に遅延を
 * 取得する線区とずれないようにするために使う。
 */
export function resolveConnectingSearchRouteIds(line: {
  route_id?: unknown;
  route_ids?: unknown;
}): string[] {
  const primary =
    typeof line.route_id === 'string' && line.route_id.trim() !== '' ? [line.route_id.trim()] : [];
  const extra = (Array.isArray(line.route_ids) ? line.route_ids : [])
    .filter((r): r is string => typeof r === 'string')
    .map((r) => r.trim())
    .filter((r) => r !== '');
  return Array.from(new Set([...primary, ...extra]));
}

/**
 * 接続線ブロック (`lines[j]`) を **ディープコピー** する。
 *
 * 「📋 複製」ボタン (`ConnectingLineBlock` → `ConnectingLineDelayByStationFields`)
 * から呼ぶ純関数。浅いコピー (`{ ...line }`) だと `directions[]` /
 * `delay_patterns[]` / `istations_lines[]` / `route_ids[]` / `*_i18n` の
 * オブジェクトが複製元と **同じ参照** になり、複製元を編集した瞬間に複製先も
 * 一緒に変わってしまう (逆も同様)。
 *
 * `structuredClone` を使わないのは、コピー対象を型で明示しておくことで
 * 「新しいフィールドを追加したときにここへの追記漏れが型エラーで気付ける」
 * ようにするため (JSON 互換でない値が将来混ざったときの事故も防げる)。
 */
export function cloneConnectingStationLine(line: ConnectingLineEntry): ConnectingLineEntry {
  const cloned: ConnectingLineEntry = {
    ...line,
    // 配列は必ず新しい配列 + 要素も新しいオブジェクトにする。
    directions: (Array.isArray(line.directions) ? line.directions : []).map((d) => {
      const clonedDirection: ConnectingDirectionEntry = { ...d };
      // `route_directions` (線区ごとの判定方面) は入れ子のオブジェクトなので、
      // 浅いコピーのままだと複製元と同じ参照を共有してしまい、
      // 複製先の方面プルダウンを変えた瞬間に複製元も一緒に変わる。
      // 元が未設定なら未設定のまま (空オブジェクトを足すと保存形が変わる)。
      if (d.route_directions && typeof d.route_directions === 'object') {
        clonedDirection.route_directions = { ...d.route_directions };
      }
      // 方面名の言語別名称 (direction_name_i18n / モンキーテスト B-1) も同様に入れ子。
      if (d.direction_name_i18n && typeof d.direction_name_i18n === 'object') {
        clonedDirection.direction_name_i18n = { ...d.direction_name_i18n };
      }
      return clonedDirection;
    }),

    delay_patterns: (Array.isArray(line.delay_patterns) ? line.delay_patterns : []).map((p) => ({
      ...p,
    })),
  };
  // 任意フィールドは **元が未設定なら未設定のまま** にする
  // (空配列 / 空オブジェクトを足すと保存形が複製元と変わってしまう)。
  if (Array.isArray(line.route_ids)) {
    cloned.route_ids = line.route_ids.slice();
  }
  if (Array.isArray(line.istations_lines)) {
    cloned.istations_lines = line.istations_lines.map((l) => ({ ...l }));
  }
  if (line.ssml_line_template_i18n && typeof line.ssml_line_template_i18n === 'object') {
    cloned.ssml_line_template_i18n = { ...line.ssml_line_template_i18n };
  }
  return cloned;
}

/**
 * 運行情報 (`train_info`) の「新規ブロック」既定値を生成する。
 *
 * 保持するのは `istations_lines` (アイステイションズ路線の複数選択) だけで、
 * 新規作成時は未選択 (キー無し) 。空のまま保存しようとするとバリデーションで弾かれる。
 */
export function createDefaultTrainInfoLine(): TrainInfoLineEntry {
  return {};
}

/**
 * 接続線遅延 (乗換駅ベース / part2) が「その他」モード (`transfer_stop_mode === 'other'`)
 * かどうかを判定する。
 *
 * 「その他」モードでは乗換駅・遅延判定を一切使わず、接続線ごとに選択した
 * アイステイションズ路線の **運行情報 (TrainInfoText) のみ** を放送する。
 *
 * 後方互換: `transfer_stop_mode` を持たない既存データは `'station'` (従来動作) 扱い。
 * 判定を 1 箇所に集約し、UI / バリデーション / SSML 補完で同じ規則を使う。
 */
export function isConnectingStationOtherMode(
  props: Partial<ConnectingLineDelayByStationProperties> | undefined,
): boolean {
  return props?.transfer_stop_mode === 'other';
}

/**
 * 接続線遅延 (乗換駅ベース / part2) の「新規方面」既定値を生成する。
 * ＋ボタンで方面を追加したときに使う。
 */

export function createDefaultConnectingStationDirection(): ConnectingDirectionEntry {
  return {
    direction_id: '',
    direction_name: '',
  };
}

/**
 * 放送粒度に対応するデフォルト SSML 文言を返すヘルパー (自列車遅延用)。
 * 粒度プルダウンを変更したタイミングで、対応する粒度のデフォルト文に
 * ssml_template を上書きする目的で使用する。
 */
export function getDefaultSelfTrainSsml(granularity: DelayBroadcastGranularity): string {
  return GRANULARITY_DEFAULT_SSML[granularity];
}

/**
 * 放送粒度に対応するデフォルト SSML 文言を返すヘルパー (接続線 transfer_aware 用)。
 */
export function getDefaultConnectingSsml(granularity: DelayBroadcastGranularity): string {
  return CONNECTING_GRANULARITY_DEFAULT_SSML[granularity];
}

export const CATEGORY_META: readonly CategoryMeta[] = [
  {
    category: 'self_train_delay',
    label: '自列車遅延',
    description: '自列車の遅延状況に応じて、遅れ分数の幅ごとに放送パターンを設定します。',
    defaultProperties: (): SelfTrainDelayProperties => ({
      delay_patterns: DEFAULT_DELAY_PATTERNS.map((p) => ({ ...p })),
    }),
  },
  {
    category: 'connecting_line_delay',
    label: '接続線遅延',
    description:
      '指定した接続線 (route_id) が遅延しているときに放送します。判定モードに応じて、接続線全体の遅延 / 折り返し列車の遅延（判定区間指定） / 乗り換え列車の遅延 を切り替えられます。',
    defaultProperties: (): ConnectingLineDelayProperties => ({
      route_id: '',
      delay_threshold_seconds: 60,
      // 既定の判定モードは「乗り換え列車の遅延」(transfer_aware)。プルダウン先頭の選択肢と揃える。
      // transfer_aware は表形式 (遅れ分数の幅 × 放送粒度) で設定するため、
      // 判定モード切替時と同じデフォルト遅延放送パターンを最初から入れておく。
      // 乗換駅 (transfer_stop_id) / 方面 (direction_id) は運用者が選ぶので既定値に含めない。
      delay_judgement_mode: 'transfer_aware',
      transfer_time_seconds: 180,
      delay_patterns: DEFAULT_CONNECTING_DELAY_PATTERNS.map((p) => ({ ...p })),
      // Issue #527: ssml_template は入れない (空欄 = 定型文マスタへ委譲)。
    }),
  },
  {
    category: 'connecting_line_delay_by_station',
    // UI 表示上のラベル。以前は「接続線遅延（乗換駅別）」だったが、旧カテゴリ
    // `connecting_line_delay` (乗換駅別ではない旧仕様) を新規作成ドロップダウンから
    // 外した結果、ユーザ向けには実質「唯一の接続線遅延カテゴリ」となったため、
    // 括弧書きを外して単に「接続線遅延」と表示する。
    // ※ サマリ表示 (`BroadcastPointsTable.resolveContentSummary`) では
    //    「接続線遅延（乗換駅: ... / 接続線 N件）」形式となり、旧カテゴリの
    //    「接続線遅延（route_id: ... / しきい値 ...秒）」形式と括弧内で区別できる。
    label: '接続線遅延',
    description:
      '乗換駅を軸に、複数の接続線 (各1〜2方面) の遅延をまとめて案内するコンテンツです。 例: 「赤羽駅からお乗換の埼京線、新宿方面は5分遅れ、大宮方面は3分遅れとなっております。」',
    defaultProperties: (): ConnectingLineDelayByStationProperties => ({
      transfer_stop_id: '',
      // Issue #527: ssml_intro_template は入れない (空欄 = 定型文マスタ
      // `connecting_station_intro` へ委譲)。
      lines: [createDefaultConnectingStationLine()],
    }),
  },
  {
    category: 'train_info',
    label: '運行情報',
    description:
      '遅延判定は行わず、選んだ路線で配信中の運行情報 (TrainInfoText) の原文だけを放送します。配信されている運行情報が無いときは放送しません。',
    defaultProperties: (): TrainInfoProperties => ({
      lines: [createDefaultTrainInfoLine()],
    }),
  },

  {
    category: 'free_text',
    label: '任意テキストの放送',
    description:
      '入力した文面をそのまま放送します。既定は「通常テキスト」で、必要に応じて SSML (`<speak>...</speak>`) や音声ファイル (MP3) に切り替えられます。',
    defaultProperties: (): FreeTextProperties => ({
      // 既定の入力方法は「通常テキスト」(input_mode='text')。
      //
      // 以前は `ssml: '<speak></speak>'` を初期値としていたため、新規作成時に
      // 入力方法が SSML で選択された状態になり、SSML を知らない利用者にとって
      // 分かりにくかった (かつ空 SSML がそのまま DB に残っていた)。
      // 通常テキストを既定とし、SSML は明示的に切り替えたときのみ使う。
      input_mode: 'text',
      text: '',
    }),
  },
];

const CATEGORY_META_BY_KEY: Record<BroadcastContentCategory, CategoryMeta> = Object.freeze(
  Object.fromEntries(CATEGORY_META.map((m) => [m.category, m])) as Record<
    BroadcastContentCategory,
    CategoryMeta
  >,
);

/** カテゴリ識別子からメタデータを取得する。未知カテゴリは undefined。 */
export function getCategoryMeta(category: string): CategoryMeta | undefined {
  return CATEGORY_META_BY_KEY[category as BroadcastContentCategory];
}

/**
 * SSML 文字列が「中身を持たない (空 SSML)」かどうかを判定する。
 *
 * 旧仕様では任意テキスト (`free_text`) の初期値が `ssml: '<speak></speak>'` だったため、
 * DB には「通常テキストで保存したのに空 SSML が残っている」レコードが存在する。
 * 空 SSML を SSML 指定ありとして扱うと、
 *   - 編集画面が意図せず SSML モードで開く
 *   - バリデーションを通ってしまい、中身のない音声が合成される
 * といった不具合になるため、「未設定」と同等に扱えるよう判定を共通化する。
 *
 * バックエンド (`lambda/update-contents/index.py` の `_is_empty_ssml`) と同じ規則
 * (`<speak>` / `</speak>` を除いて空白のみなら空) で判定する。
 *
 * @param ssml 判定対象の SSML 文字列 (undefined / null も許容)
 * @returns 未設定または中身が無い場合 true
 */
export function isEmptySsml(ssml: string | undefined | null): boolean {
  if (typeof ssml !== 'string') return true;
  return ssml.replace(/<\/?speak[^>]*>/gi, '').trim() === '';
}

/**
 * 任意テキスト放送 (free_text) の現在の入力方法を求める。
 *
 * - `input_mode` が明示されていればそれを使う (`synced` = 共有テキストも明示された場合のみ)。
 * - 無い場合は `audio_key` → audio / 中身のある `ssml` → ssml / それ以外 → text。
 *   中身のない SSML (旧初期値の `<speak></speak>`) は「SSML 未設定」として扱う。
 *
 * ⚠️ 画面側の `components/broadcastContents/logic.ts` の `resolveInputMode()` と
 * **同じ規則** であること。バリデーション (Issue #545: 言語別文面の必須チェック) は
 * 「画面に出ている入力欄」だけを対象にする必要があり、判定がズレると
 * 「見えない項目のせいで保存できない」状態になる。
 * `utils` から `components` を import すると依存方向が逆転するため、
 * ここに独立して持つ (両者の一致は `broadcastContents.test.ts` で突き合わせる)。
 */
export function resolveFreeTextInputMode(
  props: Partial<FreeTextProperties>,
): NonNullable<FreeTextProperties['input_mode']> {
  if (props.input_mode) return props.input_mode;
  if (typeof props.audio_key === 'string' && props.audio_key.trim() !== '') return 'audio';
  if (!isEmptySsml(props.ssml)) return 'ssml';
  return 'text';
}

/**
 * コンテンツ種別ごとの薄い背景色 (カード色分け用)。
 * - 自列車遅延: 薄い赤
 * - 接続線遅延 (part1 / part2 どちらも): 薄い青
 * - 任意テキスト: 薄い緑
 * 未知カテゴリはニュートラルなグレーにフォールバックする。
 */
const CATEGORY_BACKGROUND_COLORS: Record<BroadcastContentCategory, string> = {
  self_train_delay: '#fdecec',
  connecting_line_delay: '#e8f1fb',
  connecting_line_delay_by_station: '#e8f1fb',
  train_info: '#fff4e0',
  free_text: '#e9f5ec',
};

/** コンテンツ種別に対応する薄い背景色を返す (カードの色分けに使う)。 */
export function getCategoryColor(category: string): string {
  return CATEGORY_BACKGROUND_COLORS[category as BroadcastContentCategory] ?? '#f1f3f5';
}

/**
 * カテゴリの初期コンテンツを生成する。
 *
 * ⚠️ Issue #556: 言語別文面 (`*_i18n`) には **何も入れない**。
 * 以前は各言語のコード内既定訳を値として詰めていたため、新規作成した
 * コンテンツはその時点で「個別文面あり」になり、定型文マスタの多言語編集が
 * 一切反映されなかった。未入力時に放送される文面は placeholder (薄字) が示す。
 */
export function createDefaultContent(category: BroadcastContentCategory): BroadcastContent {
  const meta = CATEGORY_META_BY_KEY[category];
  return {
    category,
    languages: ['ja'],
    properties: meta.defaultProperties(),
  };
}

/**
 * BroadcastPoint から「フォームで扱うコンテンツ配列」を取り出す。
 *
 * - `contents` フィールドがあればそれを優先する (深いコピーを返す)。
 * - 無い場合は、レガシーフィールド `delay_threshold_seconds` を「自列車遅延」コンテンツへ
 *   変換した 1 件を返す (Issue #56 互換)。
 * - レガシーフィールドも無い場合は空配列を返す。
 */
export function extractContents(
  point: Pick<BroadcastPoint, 'contents' | 'delay_threshold_seconds' | 'languages'>,
): BroadcastContent[] {
  if (Array.isArray(point.contents) && point.contents.length > 0) {
    return point.contents.map((c) =>
      /*
       * Issue #556: 読み込み時に焼き込まれたコード内既定訳を剥がす。
       *
       * 既存レコードには (Issue #556 以前のコンパネが投入した) 英中韓の既定訳が
       * **値として** 入っている。放置すると個別文面がマスタより優先されたままなので、
       * 開いた時点で「空欄 (= 定型文マスタ委譲)」へ戻し、そのまま保存すれば
       * 以後マスタの多言語編集が反映されるようにする。
       * 運用者が手で書いた訳文は既定訳と一致しないため温存される。
       */
      unbakeLocalizedSsmlDefaults(
        backfillDefaultSsmlTemplates(
          normalizeSelfTrainDelayContent(
            cloneContent({
              ...c,
              ...(c.languages === undefined && point.languages?.length
                ? { languages: [...point.languages] }
                : {}),
            }),
          ),
        ),
      ),
    );
  }
  if (typeof point.delay_threshold_seconds === 'number') {
    // レガシー: delay_threshold_seconds を新形式 delay_patterns 1行に変換する。
    // 閾値秒数を分単位に変換し (切り上げ)、「1分単位」で放送するパターンとして扱う。
    const fromMinutes = Math.ceil(point.delay_threshold_seconds / 60);
    return [
      // Issue #556: 言語別文面は入れない (空欄 = 定型文マスタ委譲)。
      backfillDefaultSsmlTemplates({
        category: 'self_train_delay',
        ...(point.languages?.length ? { languages: [...point.languages] } : {}),
        properties: {
          delay_patterns: [{ delay_minutes_from: fromMinutes, granularity: '1分単位' }],
        } satisfies SelfTrainDelayProperties,
      }),
    ];
  }
  return [];
}

/**
 * 【Issue #527 で廃止】既存データの `ssml_template` が未設定 (undefined / 空文字) の場合に
 * デフォルト文言を **実値として補完** していた処理。
 *
 * ## なぜ撤去したか
 *
 * 「デフォルト文を実値として焼き込む」方式だと、保存した瞬間に DynamoDB 側が
 * 「個別文面あり」になる。個別文面は定型文マスタより優先されるため、
 * **定型文マスタを編集しても既存コンテンツの放送内容が永久に変わらなかった**。
 *
 * Issue #527 の受け入れ条件「マスタを変えたら既存の空欄コンテンツの再生内容も
 * 一括で変わる」を満たすため、**空欄 = 定型文マスタへ委譲** という保存規約へ変更した。
 * したがってフォーム読み込み時に値を書き込むことはせず、
 * 「未入力なら実際に何が放送されるか」は
 * - 入力欄の placeholder (コード内既定文)
 * - {@link resolveEffectiveSsml} による実効文面の表示
 * で伝える (= 表示専用)。
 *
 * 放送生成側 (`lambda/common/ssml_builder.py`) は空欄のとき
 * 定型文マスタ (当該言語) → 定型文マスタ (ja) → コード内既定 の 3 段で解決する。
 * API 側 (`_normalize_nested_template_translations`) も空文字を属性ごと省略するため、
 * 「空欄で保存 = マスタ委譲」がレイヤ横断で保証される。
 */
export type DefaultSsmlResolver = (templateId: string, languageKey?: string) => string | undefined;

/**
 * 定型文マスタ (`GET /phrase-templates`) の一覧から「既定文面リゾルバ」を作る。
 *
 * `(template_id, language_code)` の複合キーで索引化する。言語を省略した呼び出しは
 * 日本語 (`ja`) を引くため、日本語欄の placeholder は従来どおり 1 引数で解決できる。
 *
 * ⚠️ **言語をまたぐフォールバックは意図的に行わない** (Issue #552)。
 * 放送側 (`resolve_phrase_template`) は「当該言語 → ja」の順にフォールバックするが、
 * 画面の英語欄に日本語の文面を既定値として流し込むと、薄字 (placeholder) に
 * 「英語欄なのに日本語」が出て誤解を招く。
 * 当該言語が未登録なら、その言語のコード内既定文面を使う (呼び出し側でフォールバック)。
 *
 * ℹ️ Issue #556 以降、既定文面は `LocalizedTextFields` の **placeholder 専用**
 * (`defaultsAreValues={false}`) であり、値として保存されることはない。
 *
 * @param items 定型文マスタの一覧 (`PhraseTemplatesClient.list()` の `items`)
 * @returns `(templateId, languageKey?)` から文面を引く関数 (未登録 / 空文字は `undefined`)
 */
export function buildDefaultSsmlResolver(
  items: readonly { template_id: string; language_code: string; body?: string | null }[],
): DefaultSsmlResolver {
  // `template_id` と `language_code` を `\u0000` (テンプレート ID / 言語キーに現れない
  // 制御文字) で連結してキーにする。Map を 2 段にしないのは、参照が 1 回で済み
  // 「未登録なら undefined」の判定が単純になるため。
  const byKey = new Map<string, string>();
  for (const item of items) {
    const body = item.body;
    if (typeof body === 'string' && body.trim() !== '') {
      byKey.set(`${item.template_id}\u0000${item.language_code}`, body);
    }
  }
  return (templateId, languageKey) =>
    byKey.get(`${templateId}\u0000${languageKey ?? DEFAULT_LANGUAGE_KEY}`);
}

/**
 * 言語別の既定文面 (`LocalizedTextFields` の `defaults`) に定型文マスタを反映する
 * (Issue #552)。
 *
 * コード内既定 (`localizedDelayDefaults()` / `LOCALIZED_STATION_INTRO` など) を土台に、
 * **当該言語がマスタに登録されている場合のみ** その文面で上書きする。
 * 未登録の言語はコード内既定のまま残すため、
 * 「英語欄に日本語が入る」ような取り違えは起きない。
 *
 * @param base コード内既定の言語別文面 (`{ en: ..., zh: ..., ko: ... }`)
 * @param templateId 対応する定型文マスタの `template_id`
 * @param resolve 定型文マスタのリゾルバ (未指定 / 取得失敗時は `base` をそのまま返す)
 */
export function resolveLocalizedDefaults(
  base: Record<string, string>,
  templateId: string,
  resolve?: DefaultSsmlResolver,
): Record<string, string> {
  if (!resolve) return base;
  const next: Record<string, string> = { ...base };
  for (const languageKey of Object.keys(base)) {
    const body = resolve(templateId, languageKey);
    if (typeof body === 'string' && body.trim() !== '') {
      next[languageKey] = body;
    }
  }
  return next;
}

/**
 * 「実際に放送される文面」を解決する (表示専用 / Issue #527)。
 *
 * 放送生成側と同じ 3 段フォールバックを画面側でも再現し、
 * SSML 入力欄が空欄のときに「未入力の場合は定型文マスタの文面が使われます: 〈文面〉」
 * と提示するために使う。**DB へは書き込まない**。
 *
 * @param own 放送地点個別の文面 (最優先)
 * @param templateId 対応する定型文マスタの `template_id`
 * @param builtin コード内既定文面 (最終フォールバック)
 * @param resolve 定型文マスタのリゾルバ (未指定 / 取得失敗時は `builtin` を使う)
 */
export function resolveEffectiveSsml(
  own: string | undefined | null,
  templateId: string,
  builtin: string,
  resolve?: DefaultSsmlResolver,
): string {
  if (typeof own === 'string' && own.trim() !== '') return own;
  return resolve?.(templateId) ?? builtin;
}

/**
 * 【Issue #527 で無効化】SSML デフォルト文の実値補完。
 *
 * 空欄 = 定型文マスタ委譲へ規約変更したため、**値の書き込みは行わない**。
 * 呼び出し箇所 (`extractContents`) を残しているのは、
 * 「ここで backfill していた」という履歴と、`ssml_template` が空文字だった
 * 場合の **空文字 → 未設定 (キー削除) の正規化** を担わせるため。
 *
 * 空文字を残しても放送側の判定 (`strip()` で空なら未指定扱い) は同じだが、
 * キーを消しておくことで「マスタ委譲」であることが DB 上でも明確になり、
 * API 側の正規化 (`_normalize_nested_template_translations`) と結果が一致する。
 */
function backfillDefaultSsmlTemplates(content: BroadcastContent): BroadcastContent {
  return stripBlankSsmlTemplates(content);
}

/**
 * 空文字 (空白のみ) の SSML テンプレートをキーごと削除する (Issue #527)。
 *
 * 「空欄 = 定型文マスタ委譲」を表す正規形は **キーが無い状態** とする
 * (API 側 `_OMIT_WHEN_BLANK_TEMPLATE_FIELDS` と同じ規則)。
 * 対象は
 * - `properties.ssml_template` (自列車遅延の旧スキーマ / 接続線遅延 part1)
 * - `properties.delay_patterns[].ssml_template`
 * - `properties.ssml_intro_template` (part2 冒頭文)
 * - `properties.lines[].ssml_line_template` / `lines[].delay_patterns[].ssml_template`
 */
function stripBlankPatternSsml(pattern: DelayBroadcastPattern): DelayBroadcastPattern {
  if (typeof pattern.ssml_template !== 'string' || pattern.ssml_template.trim() !== '') {
    return pattern;
  }
  const next = { ...pattern };
  delete next.ssml_template;
  return next;
}

/**
 * part2 の上限関連キー (`max_broadcast_lines` / `over_limit_*`) を保存用の正規形へ寄せる。
 *
 * - 上限が正の整数でなければ (未設定・空・NaN・0 以下) 上限関連の 5 キーをすべて落とす。
 *   上限が無いと動作・案内文は放送側で使われないため、孤立した値を Export JSON などに
 *   残さない。
 * - 案内文は固定文を使う動作 (`replace_with_phrase` / `truncate_then_replace`) のときだけ
 *   意味を持つ。`truncate` や動作未選択のときは案内文 (日本語 / 言語別) を落とす。
 * - `replace_over_lines` は `truncate_then_replace` のときだけ残す。併用でも正の整数でなければ落とす
 *   (サーバは併用以外で付いていると 400 にするため、送らないのが正規形)。
 * - 空白のみの案内文はキーごと落とす (空欄 = 定型文マスタ委譲 / Issue #527)。
 *
 * `props` を破壊的に更新する (呼び出し側が複製済みのオブジェクトを渡す)。
 */
function normalizeOverLimitProperties(
  props: Partial<ConnectingLineDelayByStationProperties>,
): void {
  const max = props.max_broadcast_lines;
  const hasLimit = typeof max === 'number' && Number.isInteger(max) && max > 0;
  if (!hasLimit) {
    delete props.max_broadcast_lines;
    delete props.over_limit_behavior;
    delete props.replace_over_lines;
    delete props.over_limit_ssml_template;
    delete props.over_limit_ssml_template_i18n;
    return;
  }
  if (props.over_limit_behavior !== 'truncate_then_replace') {
    delete props.replace_over_lines;
  } else if (!isPositiveInteger(props.replace_over_lines)) {
    delete props.replace_over_lines;
  }
  if (!usesOverLimitPhrase(props.over_limit_behavior)) {
    delete props.over_limit_ssml_template;
    delete props.over_limit_ssml_template_i18n;
    return;
  }
  if (
    typeof props.over_limit_ssml_template === 'string' &&
    props.over_limit_ssml_template.trim() === ''
  ) {
    delete props.over_limit_ssml_template;
  }
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/** 上限超過時に固定の案内文を放送に使う動作か (固定文のみ / 併用)。 */
export function usesOverLimitPhrase(
  behavior: ConnectingStationOverLimitBehavior | undefined,
): boolean {
  return behavior === 'replace_with_phrase' || behavior === 'truncate_then_replace';
}

/**
 * 画面のプルダウン 2 つ (何路線まで遅延放送するか / 遅延が何路線を超えたら定型文にするか) の
 * 選択状態から保存用の動作を決める。`undefined` は「制限しない」。
 *
 * 両方「制限しない」は動作なし (上限なし)。1 つめだけなら打ち切り、2 つめだけなら固定文のみ、
 * 両方選んでいれば併用。
 */
export function overLimitBehaviorFromLimits(
  first: number | undefined,
  second: number | undefined,
): ConnectingStationOverLimitBehavior | undefined {
  if (first !== undefined && second !== undefined) return 'truncate_then_replace';
  if (first !== undefined) return 'truncate';
  if (second !== undefined) return 'replace_with_phrase';
  return undefined;
}

/**
 * 「上限が設定されているのに、接続線の件数以上で制限が効かない」状態か (保存時の警告用)。
 *
 * エラーにはしない (サーバも件数との整合は検証しない)。放送側は超過が起きないので
 * 「制限なし」と同じ動作になり、安全側の挙動のため。画面に警告バナーを出すだけに使う。
 */
export function isOverLimitNotEffective(
  props: Partial<ConnectingLineDelayByStationProperties>,
): boolean {
  const max = props.max_broadcast_lines;
  if (typeof max !== 'number' || !Number.isInteger(max) || max <= 0) return false;
  const lineCount = Array.isArray(props.lines) ? props.lines.length : 0;
  return max >= lineCount;
}

/**
 * 併用の 2 つめ (`replace_over_lines`) が接続線の件数以上で、固定文に切り替わる場面が来ない状態か
 * (保存時の警告用)。
 *
 * 数える対象は遅延案内文のある接続線で、その本数は接続線の件数を超えない。
 * そのため件数以上の本数では「M を超えたら」が起きない。エラーにはしない (放送は打ち切りと同じ動作)。
 * 2 つめが 1 つめ以下の不正値はエラー側で止めるので、ここでは扱わない。
 */
export function isReplaceOverLinesNotEffective(
  props: Partial<ConnectingLineDelayByStationProperties>,
): boolean {
  if (props.over_limit_behavior !== 'truncate_then_replace') return false;
  const replaceOver = props.replace_over_lines;
  if (!isPositiveInteger(replaceOver)) return false;
  const max = props.max_broadcast_lines;
  if (isPositiveInteger(max) && replaceOver <= max) return false;
  const lineCount = Array.isArray(props.lines) ? props.lines.length : 0;
  return replaceOver >= lineCount;
}

function stripBlankSsmlTemplates(content: BroadcastContent): BroadcastContent {
  if (content.category === 'self_train_delay') {
    const props = content.properties as Partial<SelfTrainDelayProperties>;
    if (!Array.isArray(props.delay_patterns)) return content;
    const nextPatterns = props.delay_patterns.map(stripBlankPatternSsml);
    // schedule / playback_rate は SSML 正規化とは独立した「BroadcastContent
    // トップレベルの任意プロパティ」。新しい content オブジェクトを構築する際に必ず
    // 引き継ぐこと (回帰防止: 引き継ぎ忘れると編集画面を開き直したときに
    // スケジュール設定や再生速度が消えて 1 倍に戻って見える現象になる)。
    return {
      category: 'self_train_delay',
      properties: { ...props, delay_patterns: nextPatterns } as SelfTrainDelayProperties,
      ...(content.languages !== undefined ? { languages: [...content.languages] } : {}),
      // schedule / playback_rate / train_type は SSML バックフィルとは独立した
      // 「BroadcastContent トップレベルの任意プロパティ」。新しい content オブジェクトを
      // 構築する際に必ず引き継ぐこと (回帰防止: 引き継ぎ忘れると編集画面を開き直したときに
      // スケジュール設定や再生速度・列車種別が消えて既定値に戻って見える現象になる)。
      ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
      ...(content.playback_rate !== undefined ? { playback_rate: content.playback_rate } : {}),
      ...(content.train_type !== undefined ? { train_type: content.train_type } : {}),
      ...(content.train_numbers !== undefined ? { train_numbers: [...content.train_numbers] } : {}),
      ...(content.enabled === false ? { enabled: false } : {}),
    };
  }
  if (content.category === 'connecting_line_delay') {
    const props = content.properties as Partial<ConnectingLineDelayProperties>;
    const mode = props.delay_judgement_mode ?? 'whole_line';
    const next: Partial<ConnectingLineDelayProperties> = { ...props };
    if (usesDelayPatternsTable(mode)) {
      // transfer_aware / departure_forecast モードは delay_patterns 側の ssml_template を扱う。
      // 単一 ssml_template (whole_line / section_scheduled 用) は使用しないので触らない。
      if (Array.isArray(props.delay_patterns)) {
        next.delay_patterns = props.delay_patterns.map((pattern) => {
          const stripped = stripBlankPatternSsml(pattern);
          // 発車見込み案内は粒度の列を隠すので、保存時に固定値へ揃える。
          return isDepartureForecastMode(mode)
            ? pinDepartureForecastGranularity(stripped)
            : stripped;
        });
      }
    } else if (typeof props.ssml_template === 'string' && props.ssml_template.trim() === '') {
      // whole_line / section_scheduled モードは単一 ssml_template。
      // 空文字は「マスタ委譲」の正規形 (キーなし) に落とす。
      delete next.ssml_template;
    }
    return {
      category: 'connecting_line_delay',
      properties: next as ConnectingLineDelayProperties,
      ...(content.languages !== undefined ? { languages: [...content.languages] } : {}),
      // schedule / playback_rate / train_type は必ず引き継ぐ
      // (詳細は self_train_delay 分岐のコメント参照)
      ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
      ...(content.playback_rate !== undefined ? { playback_rate: content.playback_rate } : {}),
      ...(content.train_type !== undefined ? { train_type: content.train_type } : {}),
      ...(content.train_numbers !== undefined ? { train_numbers: [...content.train_numbers] } : {}),
      ...(content.enabled === false ? { enabled: false } : {}),
    };
  }
  if (content.category === 'connecting_line_delay_by_station') {
    // 接続線遅延 (乗換駅ベース / part2) の SSML 正規化。
    //
    // 3層のテンプレート (intro / line / direction=delay_patterns[i].ssml_template)
    // それぞれについて、空文字を「マスタ委譲」の正規形 (キーなし) に落とす。
    // 未入力時に実際に使われる文面は
    // - intro (`ssml_intro_template`): 定型文マスタ `connecting_station_intro`
    // - line (`lines[j].ssml_line_template`): 定型文マスタ `connecting_station_line`
    // - direction (`lines[j].delay_patterns[k].ssml_template`):
    //   定型文マスタ `connecting_station_direction_*` (whole_line モードは
    //   `connecting_station_whole_line_*`)
    // で解決される (`ssml_builder._render_connecting_station_*`)。
    const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
    // 「その他」モードは乗換駅・遅延判定・各 SSML テンプレートを一切使わないため、
    // そのまま返す (使わないテンプレートを触らない)。
    if (isConnectingStationOtherMode(props)) return content;
    const nextProps: Partial<ConnectingLineDelayByStationProperties> = { ...props };
    if (typeof props.ssml_intro_template === 'string' && props.ssml_intro_template.trim() === '') {
      delete nextProps.ssml_intro_template;
    }
    normalizeOverLimitProperties(nextProps);
    if (Array.isArray(props.lines)) {
      nextProps.lines = props.lines.map((line) => {
        const nextLine = { ...line } as ConnectingLineEntry;
        if (typeof line.ssml_line_template === 'string' && line.ssml_line_template.trim() === '') {
          delete nextLine.ssml_line_template;
        }
        if (Array.isArray(line.delay_patterns)) {
          nextLine.delay_patterns = line.delay_patterns.map((pattern) => {
            const stripped = stripBlankPatternSsml(pattern);
            // 発車見込み案内は粒度の列を隠すので、保存時に固定値へ揃える。
            return isDepartureForecastMode(line.delay_judgement_mode)
              ? pinDepartureForecastGranularity(stripped)
              : stripped;
          });
        }
        return nextLine;
      });
    }
    return {
      category: 'connecting_line_delay_by_station',
      ...(content.languages !== undefined ? { languages: [...content.languages] } : {}),
      properties: nextProps as ConnectingLineDelayByStationProperties,
      // schedule / playback_rate / train_type は必ず引き継ぐ
      // (詳細は self_train_delay 分岐のコメント参照)
      ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
      ...(content.playback_rate !== undefined ? { playback_rate: content.playback_rate } : {}),
      ...(content.train_type !== undefined ? { train_type: content.train_type } : {}),
      ...(content.train_numbers !== undefined ? { train_numbers: [...content.train_numbers] } : {}),
      ...(content.enabled === false ? { enabled: false } : {}),
    };
  }
  return content;
}

/**
 * self_train_delay コンテンツを新形式に正規化する。
 * 旧形式 (delay_threshold_seconds + ssml_template) が含まれていれば
 * delay_patterns 1行に変換する。既に delay_patterns があればそのまま返す。
 */
function normalizeSelfTrainDelayContent(content: BroadcastContent): BroadcastContent {
  if (content.category !== 'self_train_delay') return content;
  const props = content.properties as Record<string, unknown>;
  // 既に新形式なら何もしない
  if (Array.isArray(props.delay_patterns)) return content;
  // 旧形式: delay_threshold_seconds から変換
  const thresholdSec =
    typeof props.delay_threshold_seconds === 'number' ? props.delay_threshold_seconds : 0;
  const fromMinutes = Math.ceil(thresholdSec / 60);
  const pattern: DelayBroadcastPattern = {
    delay_minutes_from: fromMinutes,
    granularity: '1分単位',
  };
  if (typeof props.ssml_template === 'string' && props.ssml_template.trim() !== '') {
    pattern.ssml_template = props.ssml_template;
  }
  return {
    category: 'self_train_delay',
    ...(content.languages !== undefined ? { languages: [...content.languages] } : {}),
    properties: { delay_patterns: [pattern] } satisfies SelfTrainDelayProperties,
    // schedule / playback_rate / train_type はレガシー変換の対象外 (properties 側の話) だが、
    // トップレベルの任意プロパティとして必ず引き継ぐ
    // (引き継ぎ忘れると旧スキーマデータを開いた際に設定が消えて見える)。
    ...(content.schedule !== undefined ? { schedule: content.schedule } : {}),
    ...(content.playback_rate !== undefined ? { playback_rate: content.playback_rate } : {}),
    ...(content.train_type !== undefined ? { train_type: content.train_type } : {}),
    ...(content.train_numbers !== undefined ? { train_numbers: [...content.train_numbers] } : {}),
    ...(content.enabled === false ? { enabled: false } : {}),
  };
}

/** コンテンツが有効か。`enabled` 未設定 (既存データ) は有効扱い (Issue #810)。 */
export function isContentEnabled(content: Pick<BroadcastContent, 'enabled'>): boolean {
  return content.enabled !== false;
}

/**
 * コンテンツの有効 / 無効を切り替えたコピーを返す (Issue #810)。
 * 無効 (false) のときだけ `enabled` キーを持ち、有効に戻すとキーごと削除する。
 */
export function withContentEnabled(content: BroadcastContent, enabled: boolean): BroadcastContent {
  const next = cloneContent(content);
  if (enabled) {
    delete next.enabled;
  } else {
    next.enabled = false;
  }
  return next;
}

/**
 * コンテンツの有効 / 無効から放送地点の有効 / 無効を導く (Issue #810 / コンテンツ → 一覧の連動)。
 *
 * - 全コンテンツが無効 → 地点も無効 (false)。
 * - 全コンテンツが有効 → 地点も有効 (true)。
 * - 有効 / 無効が混在 → 地点は有効 (true)。UpdateContents は地点が無効だと全コンテンツを
 *   放送しないため、一つでも有効なコンテンツがあるのに地点を無効のままにすると放送されない。
 * - コンテンツが 0 件 → `current` をそのまま返す (判断材料が無い)。
 */
export function deriveSpotEnabled(contents: BroadcastContent[], current: boolean): boolean {
  if (contents.length === 0) return current;
  return contents.some(isContentEnabled);
}

/**
 * 無効な地点を開いたとき、全コンテンツが有効扱いなら全コンテンツを無効にして返す。
 *
 * Issue #679 の時点で無効化した地点は、地点だけが `enabled=false` で、コンテンツには `enabled` が無い。
 * コンテンツへの反映はバルク切替 API (`_sync_contents_enabled`) でしか行わないため、
 * 編集で保存するまで揃わない。そのまま編集すると `deriveSpotEnabled` が「有効なコンテンツがある」と
 * 判断して地点を有効に戻し、保存で放送対象になってしまう。
 * 一つでも無効のコンテンツがあるときは、利用者が選んだ状態なので変えない。
 */
export function disableContentsOfDisabledSpot(
  contents: BroadcastContent[],
  spotEnabled: boolean,
): BroadcastContent[] {
  if (spotEnabled || contents.length === 0 || !contents.every(isContentEnabled)) return contents;
  return contents.map((content) => withContentEnabled(content, false));
}

/** コンテンツをディープコピーする (immer 等を使わず最低限) */
export function cloneContent(content: BroadcastContent): BroadcastContent {
  const cloned: BroadcastContent = {
    category: content.category,
    properties: { ...(content.properties as Record<string, unknown>) },
  };
  if (content.languages !== undefined) {
    cloned.languages = [...content.languages];
  }
  // schedule は任意プロパティ。存在すれば浅くコピー
  // (BroadcastSchedule の各値は string / string[] / TimeRange[] で、内部で mutate しないため
  //  スプレッドコピーで十分。TimeRange[] は API 送信前後で immutable に扱う)
  if (content.schedule !== undefined) {
    cloned.schedule = {
      ...content.schedule,
      days_of_week: content.schedule.days_of_week ? [...content.schedule.days_of_week] : undefined,
      time_ranges: content.schedule.time_ranges
        ? content.schedule.time_ranges.map((r) => ({ ...r }))
        : undefined,
    };
  }
  // playback_rate (再生速度倍率) も schedule と同じトップレベルの任意プロパティ。
  // プリミティブなのでそのまま代入する。
  //
  // 回帰防止: 本コピーは `ContentPropertyFields` の
  // `onChange({ ...cloneContent(content), properties: props })` 経由で
  // プロパティ編集のたびに呼ばれるため、ここで引き継ぎを忘れると
  // 「再生速度を設定した直後に任意テキストの条件を追加すると 1 倍に戻る」
  // という現象になる。
  if (content.playback_rate !== undefined) {
    cloned.playback_rate = content.playback_rate;
  }
  // train_type (列車種別) も schedule / playback_rate と同じトップレベルの任意プロパティ。
  // プリミティブなのでそのまま代入する。
  //
  // 回帰防止: 本コピーは `TrainTypeField` の onChange 自体と
  // `ContentPropertyFields` のプロパティ編集の両方から呼ばれるため、ここで
  // 引き継ぎを忘れると「快速を選んだ直後に放送文を編集すると全列車に戻る」
  // という現象になる (playback_rate で実際に起きた回帰と同じ構造)。
  if (content.train_type !== undefined) {
    cloned.train_type = content.train_type;
  }
  // train_numbers (放送対象の列車番号 / Issue #530) も schedule / playback_rate /
  // train_type と同じトップレベルの任意プロパティ。配列なので **浅いコピー** を作る
  // (呼び出し側で push / splice しても元コンテンツを壊さないため)。
  //
  // 回帰防止: 本コピーは列番入力欄の onChange 自体と `ContentPropertyFields` の
  // プロパティ編集の両方から呼ばれるため、ここで引き継ぎを忘れると
  // 「列番を設定した直後に放送文を編集すると全列車に戻る」という現象になる
  // (playback_rate / train_type で実際に起きた回帰と同じ構造)。
  if (content.train_numbers !== undefined) {
    cloned.train_numbers = [...content.train_numbers];
  }
  // enabled (コンテンツ単位の有効 / 無効 / Issue #810) もトップレベルの任意プロパティ。
  // 無効 (false) のときだけ保持する (未設定 = 有効)。引き継ぎ忘れをすると、プロパティ編集のたびに
  // 無効設定が有効に戻ってしまう (playback_rate で起きた回帰と同じ構造)。
  if (content.enabled === false) {
    cloned.enabled = false;
  }
  return cloned;
}

/**
 * unknown を BroadcastContent[] へ正規化する。Import / API レスポンスの後処理用。
 * - 配列でない / オブジェクトでない要素はスキップ。
 * - category が未知の場合もスキップ (logger 出力は呼び出し側で行う)。
 * - 言語別文面 (`*_i18n`) に焼き込まれたコード内既定訳を剥がす (Issue #556)。
 *   JSON インポートは編集画面を経由せず直接保存されるため、ここで剥がさないと
 *   旧形式でエクスポートした JSON を取り込んだ時点で「定型文マスタが反映されない」
 *   状態が復活してしまう。運用者が手で書いた訳文は既定訳と一致しないので温存される。
 */
export function normalizeContents(value: unknown): BroadcastContent[] {
  if (!Array.isArray(value)) return [];
  const result: BroadcastContent[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;
    const category = obj.category;
    if (typeof category !== 'string') continue;
    if (!getCategoryMeta(category)) continue;
    const props =
      obj.properties && typeof obj.properties === 'object'
        ? (obj.properties as Record<string, unknown>)
        : {};
    const next: BroadcastContent = {
      category: category as BroadcastContentCategory,
      properties: { ...props },
    };
    if (Array.isArray(obj.languages)) {
      next.languages = obj.languages.filter(
        (language): language is string => typeof language === 'string',
      );
    }
    // schedule は API レスポンス / import データからそのまま受け取る。
    // 詳細な形式検証 (曜日 enum / HH:mm / start<=end) はサーバ側 (`_normalize_schedule`)
    // と UI 側 (`BroadcastScheduleEditor`) で行うため、ここでは object 判定のみ実施。
    if (obj.schedule && typeof obj.schedule === 'object') {
      next.schedule = obj.schedule as BroadcastContent['schedule'];
    }
    // playback_rate (再生速度倍率) も schedule と同じくトップレベルの任意プロパティ。
    // 範囲検証 (0.5〜2.0) はサーバ側 (`_coerce_playback_rate`) と UI 側
    // (`PlaybackRateField`) で行うため、ここでは数値判定のみ実施する。
    // 引き継ぎ忘れると JSON インポート時に再生速度設定が失われる。
    if (typeof obj.playback_rate === 'number' && Number.isFinite(obj.playback_rate)) {
      next.playback_rate = obj.playback_rate;
    }
    // train_type (列車種別) も schedule / playback_rate と同じトップレベルの任意プロパティ。
    //
    // サーバ側 (`_coerce_train_type`) は `ALLOWED_TRAIN_TYPES`
    // (`all` / `rapid` / `local`) 以外を 400 にするため、インポート経路でも
    // **この 3 値以外はキーごと捨てる** (= 全列車扱い)。
    // 「保存できないデータをインポートで作らない」ことを優先する安全側の実装。
    // また `'all'` は既定値なのでキーを付けない (サーバ側も `None` に落として
    // 保存しないため、フロント / サーバで挙動が一致する)。
    // 空文字 / 空白のみ / 未知値もすべて未設定扱いになる。
    if (typeof obj.train_type === 'string') {
      const trainType = obj.train_type.trim();
      if (
        trainType !== TRAIN_TYPE_DEFAULT &&
        (ALLOWED_TRAIN_TYPES as readonly string[]).includes(trainType)
      ) {
        next.train_type = trainType as BroadcastContent['train_type'];
      }
    }
    // train_numbers (放送対象の列車番号 / Issue #530) も schedule / playback_rate /
    // train_type と同じトップレベルの任意プロパティ。
    //
    // サーバ側 (`_coerce_train_numbers`) は「半角英数字とハイフンのみ / 最大 16 文字」
    // 以外を 400 にするため、インポート経路でも **同じ規則で捨てる**
    // (「保存できないデータをインポートで作らない」ことを優先する安全側の実装)。
    // 前後空白の除去・重複除去 (大文字化して比較) もサーバと同じ規則に揃えるので、
    // インポート直後に保存しても 400 にならず、内容も変わらない。
    // 有効な要素が 1 件も無ければキーを付けない (= 全列車扱い / サーバ側も
    // `None` に落として保存しないため、フロント / サーバで挙動が一致する)。
    if (Array.isArray(obj.train_numbers)) {
      const seen = new Set<string>();
      const trainNumbers: string[] = [];
      for (const raw of obj.train_numbers) {
        if (typeof raw !== 'string') continue;
        const trimmed = raw.trim();
        if (trimmed === '') continue;
        if (trimmed.length > MAX_TRAIN_NUMBER_LENGTH) continue;
        if (!TRAIN_NUMBER_PATTERN.test(trimmed)) continue;
        const key = trimmed.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        trainNumbers.push(trimmed);
        if (trainNumbers.length >= MAX_TRAIN_NUMBERS) break;
      }
      if (trainNumbers.length > 0) {
        next.train_numbers = trainNumbers;
      }
    }
    // Issue #810: サーバ (`_normalize_contents`) も false のときだけ保存するので、ここも合わせる。
    if (obj.enabled === false) {
      next.enabled = false;
    }
    // Issue #556: 焼き込まれたコード内既定訳を剥がす (空欄 = 定型文マスタ委譲)。
    result.push(unbakeLocalizedSsmlDefaults(next));
  }
  return result;
}

/**
 * バリデーション。問題があれば日本語エラー文字列を返す (null = 問題なし)。
 *
 * **後方互換のための薄いラッパー**。内部では全件収集する
 * {@link validateContentIssues} を呼び、その先頭 issue の `message` を返す。
 * 文言は従来と完全に同一なので、既存のユニットテスト / E2E はそのまま通る。
 *
 * 新規コードは「どの項目でエラーになったか」を UI で案内できる
 * {@link validateContentIssues} を使うこと。
 */
export function validateContent(content: BroadcastContent, index: number): string | null {
  return firstIssueMessage(validateContentIssues(content, index));
}

/**
 * コンテンツ 1 件のバリデーションエラーを **すべて** 収集する。
 *
 * 従来の `validateContent` は最初の 1 件で early return していたため、
 * ユーザーは「直す → 保存 → 次のエラー」を繰り返す必要があった。
 * 本関数は全件を `ValidationIssue[]` として返し、各 issue に
 * 「どのコンテンツ / 接続線 / 方面 / パターン行の、どの入力欄か」
 * (`target`) を持たせる。UI 側はこれを使って
 *   - エラー一覧をクリック可能なサマリとして表示する
 *   - 折りたたみ / タブを自動で開いて該当欄までスクロールする
 *   - 該当欄に赤枠 (`.is-invalid`) を付ける
 * ことができる。
 *
 * `message` は従来の文言と 1 文字も変えていない (回帰防止)。
 *
 * @param content 検証対象のコンテンツ
 * @param index `contents[]` 内の 0 始まり index (メッセージの「コンテンツ #N」に使う)
 */
export function validateContentIssues(content: BroadcastContent, index: number): ValidationIssue[] {
  const prefix = `コンテンツ #${index + 1}`;
  const issues: ValidationIssue[] = [];
  /** issue を 1 件積む (target には常に contentIndex を含める) */
  const push = (message: string, target: Omit<ValidationTarget, 'contentIndex'> = {}): void => {
    issues.push({ message, target: { contentIndex: index, ...target } });
  };

  /*
   * Issue #555: 放送言語を 1 件も選択しないまま保存できてしまう問題。
   *
   * `languages: []` は放送側で「このコンテンツはどの言語でも放送しない」と
   * 解釈されるため、保存できても **そのコンテンツは永久に放送されない**。
   * 設定したのに何も起きないのはエラーにならず静かに壊れる典型なので、
   * 保存前に弾いて気付けるようにする (日本語のみを含め最低 1 言語は必須)。
   *
   * ⚠️ `languages` プロパティ自体が無い既存データは「日本語のみ放送」
   * (後方互換) なので **対象外**。明示的な空配列だけを弾く。
   * バックエンド (`lambda/control-panel-api/index.py`) も同じ規則で 400 を返す。
   */
  if (Array.isArray(content.languages) && content.languages.length === 0) {
    push(`${prefix}: 放送言語を 1 つ以上選択してください（未選択のままだと放送されません）`);
  }

  /*
   * Issue #555 (不具合 C): SSML の整形式チェック。
   *
   * 「日本語から翻訳」の機械翻訳が `</speak>` の外側へ句読点を出すことがあり、
   * そのまま保存すると TTS がエラー / 意図しない読み上げになる。
   * 翻訳結果は「下書き」として人の目に触れるが気付かず保存できてしまうため、
   * 保存前に赤枠 + エラーサマリで示す。
   */
  collectSsmlWellFormedIssues(content, prefix, index, push);

  switch (content.category) {
    case 'self_train_delay': {
      const props = content.properties as Partial<SelfTrainDelayProperties>;
      if (!Array.isArray(props.delay_patterns) || props.delay_patterns.length === 0) {
        push(`${prefix} (自列車遅延): 遅延放送パターンを 1 件以上設定してください`);
        return issues;
      }
      for (let i = 0; i < props.delay_patterns.length; i++) {
        const pattern = props.delay_patterns[i] as Partial<DelayBroadcastPattern>;
        // 行内の各エラーは独立して報告する (「開始分数」と「SSML」の両方が
        // 不正なら 2 件出す)。どちらも同じ行 (patternIndex) に紐づく。
        if (
          typeof pattern.delay_minutes_from !== 'number' ||
          Number.isNaN(pattern.delay_minutes_from) ||
          pattern.delay_minutes_from < 0 ||
          !Number.isInteger(pattern.delay_minutes_from)
        ) {
          push(
            `${prefix} (自列車遅延) パターン${i + 1}: 遅れ開始分数を 0 以上の整数で指定してください`,
            { patternIndex: i, testId: `self-train-delay-ssml-template-${i}` },
          );
        } else if (pattern.delay_minutes_to !== undefined) {
          // 開始分数が妥当なときだけ終了分数との大小を比較する
          // (従来も開始分数エラー時は終了分数を見ずに return していた)。
          if (
            typeof pattern.delay_minutes_to !== 'number' ||
            Number.isNaN(pattern.delay_minutes_to) ||
            !Number.isInteger(pattern.delay_minutes_to) ||
            pattern.delay_minutes_to <= pattern.delay_minutes_from
          ) {
            push(
              `${prefix} (自列車遅延) パターン${i + 1}: 遅れ終了分数は開始分数より大きい整数で指定してください`,
              { patternIndex: i, testId: `self-train-delay-ssml-template-${i}` },
            );
          }
        }
        // Issue #527: SSML 入力欄は **空欄可** (空欄 = 定型文マスタへ委譲)。
        // 空欄のときは放送側が「定型文マスタ (当該言語 → ja) → コード内既定」の
        // 3 段で粒度別デフォルト文面を解決するため、無音にはならない。
        // マスタを編集すれば空欄コンテンツの放送内容が一括で変わる仕様なので、
        // ここで必須にすると (実値が焼き込まれ) その一括反映が効かなくなる。
      }
      return issues;
    }

    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      /*
       * Issue #585: 接続線の線区愛称 (part1) の言語別名称の入力漏れ。
       *
       * 日本語の愛称だけを入れて保存すると `{route_name}` に日本語が混入した
       * 外国語文面が生成され、外国語 Polly ボイスがそれを読み上げてしまう。
       * 文面 (Issue #545) と同じ検査を愛称へ横展開する。
       */
      collectRequiredRouteNicknameIssues(
        props,
        content.languages ?? [DEFAULT_LANGUAGE_KEY],
        'connecting-line-route-nickname',
        `connecting-line-route-nickname-${index}`,
        `${prefix} (接続線遅延)`,
        push,
      );
      if (typeof props.route_id !== 'string' || props.route_id.trim() === '') {
        // part1 の route_id セレクタには data-testid が無く aria-label のみなので
        // testId は付けない (該当コンテンツカードまでのスクロールにフォールバックする)。
        push(`${prefix} (接続線遅延): 接続線の route_id を指定してください`);
      }
      if (
        typeof props.delay_threshold_seconds !== 'number' ||
        Number.isNaN(props.delay_threshold_seconds) ||
        props.delay_threshold_seconds < 0
      ) {
        push(`${prefix} (接続線遅延): 遅延しきい値秒数を 0 以上で指定してください`);
      }
      // 判定モード別の必須項目チェック。
      const mode = props.delay_judgement_mode ?? 'whole_line';
      if (mode === 'section_scheduled') {
        // 折り返し列車の遅延（判定区間指定）モードは開始駅・終了駅が必須。
        const hasStart =
          typeof props.start_stop_id === 'string' && props.start_stop_id.trim() !== '';
        const hasEnd = typeof props.end_stop_id === 'string' && props.end_stop_id.trim() !== '';
        if (!hasStart || !hasEnd) {
          push(
            `${prefix} (接続線遅延): 「折り返し列車の遅延（判定区間指定）」モードでは遅延判定開始駅と終了駅を指定してください`,
            // 開始駅が未入力ならそちらを、開始済みなら終了駅を指し示す。
            { testId: hasStart ? 'delay-end-stop-select' : 'delay-start-stop-select' },
          );
        }
      }
      if (usesDelayPatternsTable(mode)) {
        // 乗換列車モード / 発車見込み案内モードは乗換駅と乗換時間が必須
        // (接続便の特定が共通のため)。
        const modeLabel = isDepartureForecastMode(mode)
          ? '接続線の発車見込み案内'
          : '乗り換え列車の遅延';
        const hasTransferStop =
          typeof props.transfer_stop_id === 'string' && props.transfer_stop_id.trim() !== '';
        if (!hasTransferStop) {
          push(`${prefix} (接続線遅延): 「${modeLabel}」モードでは乗換駅を指定してください`, {
            testId: 'transfer-stop-select',
          });
        }
        if (
          typeof props.transfer_time_seconds !== 'number' ||
          Number.isNaN(props.transfer_time_seconds) ||
          props.transfer_time_seconds < 0 ||
          !Number.isInteger(props.transfer_time_seconds)
        ) {
          push(
            `${prefix} (接続線遅延): 「${modeLabel}」モードでは乗換時間 (秒) を 0 以上の整数で指定してください`,
            { testId: 'transfer-time-seconds-input' },
          );
        }
        // 「乗り換え列車の遅延」モードは遅れ分数の幅ごとに粒度を切り替える表形式で
        // 設定する。delay_patterns が指定されていれば自列車遅延と同じ整合性チェックを行う。
        // delay_patterns 未指定の場合は旧スキーマ (delay_threshold_seconds + 単一 ssml_template)
        // で動作するため、ここではエラーにしない (後方互換)。
        //
        // 発車見込み案内は旧スキーマが無く、delay_patterns が未設定 / 空だと
        // **バックエンドは放送しない** ため、未設定でも必須エラーにする。
        if (props.delay_patterns !== undefined || isDepartureForecastMode(mode)) {
          if (!Array.isArray(props.delay_patterns) || props.delay_patterns.length === 0) {
            push(
              `${prefix} (接続線遅延): 「${modeLabel}」モードでは遅延放送パターンを 1 件以上設定してください`,
            );
          } else {
            for (let i = 0; i < props.delay_patterns.length; i++) {
              const pattern = props.delay_patterns[i] as Partial<DelayBroadcastPattern>;
              // part1 の transfer_aware 表は `connecting-line-delay-ssml-template-{i}`。
              const rowTestId = `connecting-line-delay-ssml-template-${i}`;
              if (
                typeof pattern.delay_minutes_from !== 'number' ||
                Number.isNaN(pattern.delay_minutes_from) ||
                pattern.delay_minutes_from < 0 ||
                !Number.isInteger(pattern.delay_minutes_from)
              ) {
                push(
                  `${prefix} (接続線遅延) パターン${i + 1}: 遅れ開始分数を 0 以上の整数で指定してください`,
                  { patternIndex: i, testId: rowTestId },
                );
              } else if (pattern.delay_minutes_to !== undefined) {
                if (
                  typeof pattern.delay_minutes_to !== 'number' ||
                  Number.isNaN(pattern.delay_minutes_to) ||
                  !Number.isInteger(pattern.delay_minutes_to) ||
                  pattern.delay_minutes_to <= pattern.delay_minutes_from
                ) {
                  push(
                    `${prefix} (接続線遅延) パターン${i + 1}: 遅れ終了分数は開始分数より大きい整数で指定してください`,
                    { patternIndex: i, testId: rowTestId },
                  );
                }
              }
              // Issue #527: 各行の SSML 入力欄は **空欄可** (空欄 = 定型文マスタへ委譲)。
              // 未入力なら定型文マスタ `connecting_delay_{粒度}` → ja → コード内既定へ
              // フォールバックする (無音にはならない)。
              void rowTestId;
            }
          }
        }
      }
      // Issue #527: whole_line / section_scheduled の単一 ssml_template も **空欄可**。
      // 空欄なら定型文マスタ `connecting_delay_whole_line` /
      // `connecting_delay_section_scheduled` → `connecting_delay` → コード内既定を使う。
      return issues;
    }

    case 'connecting_line_delay_by_station': {
      // 接続線遅延 (乗換駅ベース / part2) のバリデーション。
      // 三層構造 (乗換駅コンテンツ → 接続線ブロック → 方面) と、接続線ブロックごとの
      // delay_patterns (遅れ分数の幅 × 放送粒度 × 方面部分文SSML) をすべてチェックする。
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      // レガシー: 旧「その他」モード (`transfer_stop_mode === 'other'`) で保存済みのデータ。
      // UI からは設定できないが、開いて保存し直せるよう乗換駅・遅延判定系の必須チェックを
      // 免除し、代わりに「接続線ごとにアイステイションズ路線が 1 件以上」を必須とする。
      if (isConnectingStationOtherMode(props)) {
        collectTrainInfoIssues(props, prefix, push);
        return issues;
      }

      if (typeof props.transfer_stop_id !== 'string' || props.transfer_stop_id.trim() === '') {
        push(`${prefix} (接続線遅延／乗換駅別): 乗換駅を指定してください`, {
          testId: 'connecting-station-transfer-stop-select',
        });
      } else {
        /*
         * モンキーテスト B-3: 乗換駅名の言語別名称の入力漏れ。
         *
         * 方面名 (B-1) / 線区愛称 (Issue #585) と同じ規則。サーバ側
         * `_validate_transfer_stop_name_localized` と同じ判定にするため、
         * `collectRouteNicknameLocalizedIssues` を流用する。
         */
        collectRouteNicknameLocalizedIssues(
          {
            route_nickname: props.transfer_stop_id,
            route_nickname_i18n: props.transfer_stop_name_i18n,
          },
          content.languages ?? [DEFAULT_LANGUAGE_KEY],
          '乗換駅名',
          'connecting-station-transfer-stop-name',
          `${prefix} (接続線遅延／乗換駅別)`,
          push,
        );
      }
      /*
       * 上限 (`max_broadcast_lines`) を設定したら、超過時の動作の選択は必須。
       * サーバ (`control-panel-api`) も動作なしの上限を 400 にするため、
       * 「保存を押したら 400」ではなく画面で先に赤枠で気付けるようにする。
       * 画面は本数から動作を決めるため、この状態になるのは読み込んだ旧データなどに限る。
       */
      if (
        props.max_broadcast_lines !== undefined &&
        props.max_broadcast_lines !== null &&
        props.over_limit_behavior !== 'replace_with_phrase' &&
        props.over_limit_behavior !== 'truncate' &&
        props.over_limit_behavior !== 'truncate_then_replace'
      ) {
        push(
          `${prefix} (接続線遅延／乗換駅別): 遅延放送の路線数の設定が不完全です。「何路線まで遅延放送するか」「遅延が何路線を超えたら定型文にするか」を選び直してください`,
          { testId: 'connecting-station-max-broadcast-lines' },
        );
      }
      /*
       * 動作を持っているのに本数が無い旧データは保存させない (画面の操作では作られない)。
       * 動作ごとに本数の入力欄が違う:
       *   - 打ち切り / 併用: 1 つめ「何路線まで遅延放送するか」(`max_broadcast_lines`)
       *   - 固定文のみ: 2 つめ「遅延が何路線を超えたら定型文にするか」(`max_broadcast_lines` を保存する欄)
       * 併用の 2 つめ (`replace_over_lines`) は、1 つめより大きい値に限る。
       * サーバも同じ条件で 400 にするため、保存を押す前に画面で赤枠にする。
       * 件数以上の本数は警告だけでここでは止めない。
       */
      const max = props.max_broadcast_lines;
      if (props.over_limit_behavior === 'truncate' && !isPositiveInteger(max)) {
        push(
          `${prefix} (接続線遅延／乗換駅別): 打ち切るときは、「何路線まで遅延放送するか」を選択してください`,
          { testId: 'connecting-station-max-broadcast-lines' },
        );
      }
      if (props.over_limit_behavior === 'replace_with_phrase' && !isPositiveInteger(max)) {
        push(
          `${prefix} (接続線遅延／乗換駅別): 定型文に差し替えるときは、「遅延が何路線を超えたら定型文にするか」を選択してください`,
          { testId: 'connecting-station-replace-over-lines' },
        );
      }
      if (props.over_limit_behavior === 'truncate_then_replace') {
        const replaceOver = props.replace_over_lines;
        if (!isPositiveInteger(max)) {
          push(
            `${prefix} (接続線遅延／乗換駅別): 打ち切りと定型文を併用するときは、「何路線まで遅延放送するか」を選択してください`,
            { testId: 'connecting-station-max-broadcast-lines' },
          );
        }
        if (!isPositiveInteger(replaceOver)) {
          push(
            `${prefix} (接続線遅延／乗換駅別): 打ち切りと定型文を併用するときは、「遅延が何路線を超えたら定型文にするか」を選択してください`,
            { testId: 'connecting-station-replace-over-lines' },
          );
        } else if (isPositiveInteger(max) && replaceOver <= max) {
          push(
            `${prefix} (接続線遅延／乗換駅別): 「遅延が何路線を超えたら定型文にするか」は、「何路線まで遅延放送するか」(${max} 路線) より大きい値を選択してください`,
            { testId: 'connecting-station-replace-over-lines' },
          );
        }
      }
      if (!Array.isArray(props.lines) || props.lines.length === 0) {
        push(`${prefix} (接続線遅延／乗換駅別): 接続線を 1 件以上追加してください`, {
          testId: 'connecting-station-add-line-button',
        });
        // Issue #527: 冒頭 SSML テンプレートは空欄可 (定型文マスタ委譲) になったため、
        // 接続線が無い場合はここで終わる。
        return issues;
      }

      for (let li = 0; li < props.lines.length; li++) {
        const line = props.lines[li] as Partial<ConnectingLineEntry> | undefined;
        const linePrefix = `${prefix} (接続線遅延／乗換駅別) 接続線${li + 1}`;
        if (!line || typeof line !== 'object') {
          push(`${linePrefix}: 接続線の設定が不正です`, { lineIndex: li });
          // 接続線オブジェクト自体が壊れている場合、以降の項目は評価できない。
          continue;
        }
        if (typeof line.route_id !== 'string' || line.route_id.trim() === '') {
          push(`${linePrefix}: 接続線 (route_id) を指定してください`, {
            lineIndex: li,
            testId: `connecting-line-route-id-${li}`,
          });
        }
        /*
         * Issue #585: 接続線ブロックごとの線区愛称の言語別名称の入力漏れ。
         *
         * 接続線遅延は接続線ごとに愛称を持つため、日本語だけ入力して保存する
         * ケースが最も起こりやすい経路。放置すると `{route_name}` に日本語が
         * 混入した外国語文面を外国語 Polly ボイスが読み上げる。
         */
        collectRequiredRouteNicknameIssues(
          line,
          content.languages ?? [DEFAULT_LANGUAGE_KEY],
          `connecting-line-route-nickname-${li}`,
          `connecting-line-route-nickname-${li}`,
          linePrefix,
          push,
          { lineIndex: li },
        );
        // 遅延検索対象の線区 (route_ids / 任意・複数指定) の妥当性。
        // サーバ側 `_normalize_connecting_route_ids` が 400 にする条件と揃え、
        // 「保存を押したら 400」ではなく画面で先に気付けるようにする (二重防御)。
        collectConnectingRouteIdsIssues(line.route_ids, linePrefix, li, push);
        if (
          line.delay_judgement_mode !== 'whole_line' &&
          line.delay_judgement_mode !== 'section_scheduled' &&
          line.delay_judgement_mode !== 'transfer_aware' &&
          line.delay_judgement_mode !== 'departure_forecast'
        ) {
          push(`${linePrefix}: 判定モードを選択してください`, {
            lineIndex: li,
            testId: `connecting-line-judgement-mode-${li}`,
          });
        }
        // 判定モードが transfer_aware / departure_forecast のときは乗換時間 (秒) が必要
        // (接続便の特定が共通のため)。
        if (usesDelayPatternsTable(line.delay_judgement_mode)) {
          const modeLabel = isDepartureForecastMode(line.delay_judgement_mode)
            ? '接続線の発車見込み案内'
            : '乗り換え列車の遅延';
          if (
            typeof line.transfer_time_seconds !== 'number' ||
            Number.isNaN(line.transfer_time_seconds) ||
            line.transfer_time_seconds < 0 ||
            !Number.isInteger(line.transfer_time_seconds)
          ) {
            push(
              `${linePrefix}: 「${modeLabel}」モードでは乗換時間 (秒) を 0 以上の整数で指定してください`,
              { lineIndex: li, testId: `connecting-line-transfer-time-${li}` },
            );
          }
        }

        // 方面 (directions) は 1〜2 件必須。
        //
        // ただし判定モードが `whole_line` (接続線全体の遅延) のときは
        // **方面の入力自体が不要** (`requiresConnectingStationDirections`)。
        // 接続線全体の最大遅延だけで判定し、放送文でも方面名を読み上げないため、
        // UI でも方面セクションを表示していない。「画面に無い項目が原因で保存できない」
        // 状態を避けるため必須チェックを免除する
        // (方面は放送・判定の繰り返し単位として内部的に 1 件保持される)。
        const requiresDirections = requiresConnectingStationDirections(line.delay_judgement_mode);
        if (!Array.isArray(line.directions) || line.directions.length === 0) {
          if (requiresDirections) {
            push(`${linePrefix}: 方面を 1 件以上追加してください`, {
              lineIndex: li,
              testId: `connecting-direction-add-${li}`,
            });
          }
        } else if (line.directions.length > 2) {
          push(`${linePrefix}: 方面は最大 2 件までです`, { lineIndex: li });
        }
        for (let di = 0; requiresDirections && di < (line.directions?.length ?? 0); di++) {
          const dir = line.directions?.[di] as Partial<ConnectingDirectionEntry> | undefined;
          const dirPrefix = `${linePrefix} 方面${di + 1}`;
          if (!dir || typeof dir !== 'object') {
            push(`${dirPrefix}: 方面の設定が不正です`, { lineIndex: li, directionIndex: di });
            continue;
          }
          if (typeof dir.direction_name !== 'string' || dir.direction_name.trim() === '') {
            // 案内文面用の方面名は放送文の主要トークンなので必須。
            push(`${dirPrefix}: 案内文面用の方面名 (例:「新宿」) を入力してください`, {
              lineIndex: li,
              directionIndex: di,
              testId: `connecting-direction-name-${li}-${di}`,
            });
          } else {
            /*
             * モンキーテスト B-1: 方面名の言語別名称の入力漏れ。
             *
             * 線区愛称 (Issue #585) と同じ規則 (日本語の名称が入っているときだけ、
             * 放送言語の名称を必須にする)。サーバ側 `_validate_direction_name_localized`
             * と同じ判定にするため、`collectRouteNicknameLocalizedIssues` を流用する。
             */
            collectRouteNicknameLocalizedIssues(
              {
                route_nickname: dir.direction_name,
                route_nickname_i18n: dir.direction_name_i18n,
              },
              content.languages ?? [DEFAULT_LANGUAGE_KEY],
              '方面名',
              `connecting-direction-name-${li}-${di}`,
              dirPrefix,
              push,
              { lineIndex: li, directionIndex: di },
            );
          }
          // 線区ごとの判定方面 (route_directions / 任意) の妥当性。
          // サーバ側 `_normalize_direction_overrides` が 400 にする条件と揃える
          // (JSON インポート経由の値は UI のプルダウンを通らないため二重防御が必要)。
          collectRouteDirectionsIssues(dir.route_directions, dirPrefix, li, di, push);
          // section_scheduled モードでは方面ごとに区間 (start/end_stop_id) が必須。
          if (line.delay_judgement_mode === 'section_scheduled') {
            const hasStart =
              typeof dir.start_stop_id === 'string' && dir.start_stop_id.trim() !== '';
            const hasEnd = typeof dir.end_stop_id === 'string' && dir.end_stop_id.trim() !== '';
            if (!hasStart || !hasEnd) {
              push(
                `${dirPrefix}: 「折り返し列車の遅延（判定区間指定）」モードでは遅延判定開始駅と終了駅を指定してください`,
                {
                  lineIndex: li,
                  directionIndex: di,
                  // 未入力側の駅セレクトを指し示す。
                  testId: hasStart
                    ? `connecting-direction-end-stop-${li}-${di}`
                    : `connecting-direction-start-stop-${li}-${di}`,
                },
              );
            }
          }
        }
        // 接続線ブロックごとの遅延放送パターン。空 or 各行の SSML 未入力を検出する。
        if (!Array.isArray(line.delay_patterns) || line.delay_patterns.length === 0) {
          push(`${linePrefix}: 遅延放送パターンを 1 件以上設定してください`, {
            lineIndex: li,
            testId: `connecting-station-line-delay-patterns-${li}`,
          });
        } else {
          for (let pi = 0; pi < line.delay_patterns.length; pi++) {
            const pattern = line.delay_patterns[pi] as Partial<DelayBroadcastPattern>;
            // part2 の方面部分文 SSML 欄は
            // `connecting-station-line-{lineIndex}-direction-ssml-{patternIndex}`。
            const rowTestId = `connecting-station-line-${li}-direction-ssml-${pi}`;
            if (
              typeof pattern.delay_minutes_from !== 'number' ||
              Number.isNaN(pattern.delay_minutes_from) ||
              pattern.delay_minutes_from < 0 ||
              !Number.isInteger(pattern.delay_minutes_from)
            ) {
              push(
                `${linePrefix} パターン${pi + 1}: 遅れ開始分数を 0 以上の整数で指定してください`,
                { lineIndex: li, patternIndex: pi, testId: rowTestId },
              );
            } else if (pattern.delay_minutes_to !== undefined) {
              if (
                typeof pattern.delay_minutes_to !== 'number' ||
                Number.isNaN(pattern.delay_minutes_to) ||
                !Number.isInteger(pattern.delay_minutes_to) ||
                pattern.delay_minutes_to <= pattern.delay_minutes_from
              ) {
                push(
                  `${linePrefix} パターン${pi + 1}: 遅れ終了分数は開始分数より大きい整数で指定してください`,
                  { lineIndex: li, patternIndex: pi, testId: rowTestId },
                );
              }
            }
            // Issue #527: 方面部分文の SSML は **空欄可** (空欄 = 定型文マスタへ委譲)。
            // 未入力なら `connecting_station_direction_{粒度}`
            // (whole_line モードは `connecting_station_whole_line_{粒度}`) → ja →
            // コード内既定へフォールバックする。
            void rowTestId;
          }
        }
        // Issue #527: 接続線ブロック SSML テンプレートも **空欄可**
        // (空欄なら定型文マスタ `connecting_station_line` → ja → コード内既定)。
      }
      // Issue #527: 冒頭 SSML テンプレートも **空欄可**
      // (空欄なら定型文マスタ `connecting_station_intro` → ja → コード内既定)。
      return issues;
    }

    case 'free_text': {
      const props = content.properties as Partial<FreeTextProperties>;
      /*
       * 共有テキスト (`input_mode === 'synced'`): 本文は保存せずマスタを参照する。
       *
       * 検査するのは「参照先が選ばれている」「日本語のみ」「放送条件」だけ。
       * 通常テキスト / SSML / 音声ファイル用の必須・文字数・言語別文面の検査は
       * 画面に出ていない入力欄なので行わない (見えない項目のせいで保存できない状態を作らない)。
       * サーバ (`_validate_free_text_synced_ref`) と同じ規則にする。
       */
      if (resolveFreeTextInputMode(props) === 'synced') {
        collectSyncedTextIssues(content, props, prefix, push);
        return issues;
      }
      const hasText = typeof props.text === 'string' && props.text.trim() !== '';
      // 中身のない SSML (旧初期値の `<speak></speak>` など) は「SSML 未入力」として扱う。
      // これを「入力あり」と判定してしまうと、通常テキストが未入力のまま保存でき、
      // 放送時に中身のない (再生できない) 音声が合成されてしまう。
      // バックエンド (`_is_empty_ssml`) と同じ規則で判定する。
      const hasSsml = !isEmptySsml(props.ssml);
      const hasAudio = typeof props.audio_key === 'string' && props.audio_key.trim() !== '';
      // 通常テキスト / SSML / 音声ファイル (audio_key) のいずれかがあれば良い。
      if (!hasText && !hasSsml && !hasAudio) {
        push(
          `${prefix} (任意テキスト): テキストか SSML を入力するか、音声ファイルをアップロードしてください`,
          {
            // 既定の入力方法は「通常テキスト」なのでそちらの欄を指し示す
            // (SSML / 音声モードで開いていても同じカードにスクロールできる)。
            testId: 'content-free-text-plain',
          },
        );
      }
      /*
       * Issue #545: 選択した言語の **言語別文面が未入力のまま保存できてしまう** 問題。
       *
       * 放送側は当該言語の文面が無いと日本語 (`text` / `ssml` / `audio_key`) へ
       * フォールバックするため、「英語を選んだのに日本語が流れる」という
       * **エラーにならず静かに間違った案内をする** 壊れ方になる。
       * 保存前に「選択したのに未入力の言語」を列挙してユーザーへ返す。
       *
       * ⚠️ 遅延放送 (自列車 / 接続線) は空欄 = 定型文マスタへ委譲という規約
       * (Issue #527) なので **必須にしてはいけない**。任意テキストはマスタを
       * 持たないため、ここだけが必須チェックの対象になる。
       */
      collectFreeTextLocalizedIssues(content, props, prefix, index, push);
      /*
       * 日本語の通常テキストの文字数上限。言語別文面と同じ上限で弾く。
       * 前後の空白を除いてコードポイントで数える (サーバの `len(text.strip())` と同じ規則)。
       * `String.length` は UTF-16 単位で結合文字・絵文字が 2 と数えられ、サーバと食い違うため使わない。
       */
      if (typeof props.text === 'string') {
        const length = countTextCharacters(props.text);
        if (length > MAX_FREE_TEXT_LENGTH) {
          push(
            `${prefix} (任意テキスト): 放送文面 (通常テキスト) は${MAX_FREE_TEXT_LENGTH}文字以内で入力してください` +
              `（現在 ${length} 文字。超過すると放送できません）`,
            { testId: 'content-free-text-plain' },
          );
        }
      }
      // 外国語の文面 (`text_i18n` / `ssml_i18n`) も日本語と同じ上限・同じ見せ方で弾く。
      collectFreeTextLocalizedLengthIssues(props, prefix, index, push);
      // 放送条件 (任意・複数可) のチェック。
      // - 未指定 / 空配列は「無条件放送」として許可する (後方互換)。
      // - 指定されている場合は各条件について接続線遅延と同等の必須項目チェックを行う。
      if (Array.isArray(props.conditions) && props.conditions.length > 0) {
        for (let ci = 0; ci < props.conditions.length; ci++) {
          const cond = props.conditions[ci] as Partial<BroadcastCondition> | undefined;
          const condErr = validateBroadcastCondition(
            cond,
            `${prefix} (任意テキスト) 条件${ci + 1}`,
          );
          if (condErr) {
            // 放送条件の各行は `broadcast-condition-row-{index}` で特定できる。
            // 条件内のどのフィールドかまでは辿らず、行までのスクロールに留める
            // (条件行はコンパクトなので行が見えれば十分に発見できる)。
            push(condErr, {
              conditionIndex: ci,
              testId: `broadcast-condition-row-${ci}`,
            });
          }
        }
      }
      return issues;
    }

    case 'train_info': {
      // 運行情報のみ。遅延判定・乗換駅・SSML は使わないので、「接続線が 1 件以上」と
      // 「各接続線にアイステイションズ路線が 1 件以上」だけを必須とする。
      const props = content.properties as Partial<TrainInfoProperties>;
      collectTrainInfoIssues(props, prefix, push);
      return issues;
    }

    default:
      push(`${prefix}: 未対応のカテゴリ ${(content as { category: string }).category}`);
      return issues;
  }
}

/**
 * SSML の **整形式でない文面** を収集する (Issue #555 / 不具合 C)。
 *
 * ## なぜ必要か
 *
 * 「日本語から翻訳」ボタンの機械翻訳が
 *
 *     <speak>… We apologize for any inconvenience this may have caused</speak>.
 *                                                          ↑ 句点が </speak> の外
 *
 * のように **`</speak>` の外側にテキストがある SSML** を返すことがあった。
 * この形は Polly (TTS) でエラー / 意図しない読み上げになる。
 *
 * 翻訳結果は「下書き」として人の目に触れるが、**気付かず保存できてしまう** と
 * 実放送で当該言語だけ壊れる。エラーにならず静かに壊れる経路なので、
 * 保存前に該当欄を赤枠 + エラーサマリで示す。
 *
 * ## プレーンテキストは対象外
 *
 * 放送側は `<speak>` を含まない文面を自動でラップするため、`<speak>` を
 * 1 つも含まない文面は正しい入力 (`isWellFormedSsml` が true)。
 * ここを弾くと既存データ・定型文マスタの文面が保存できなくなる。
 *
 * ## 検査対象
 *
 * 「日本語から翻訳」ボタンが値を書き込む先すべて。日本語 (既定フィールド) と
 * 言語別文面 (`*_i18n`) の両方を、ネスト (`delay_patterns` / `lines`) の奥まで辿る。
 * 検査漏れがあるとその欄経由で壊れた SSML が本番へ入る。
 *
 * ⚠️ バックエンド (`lambda/control-panel-api/index.py`) も同じ規則で 400 を返す。
 *
 * @param content 検証対象のコンテンツ
 * @param prefix エラーメッセージの接頭辞 (例: 「コンテンツ #1」)
 * @param index `contents[]` の 0 始まり index (testId の組み立てに使う)
 * @param push issue を積むコールバック
 */
function collectSsmlWellFormedIssues(
  content: BroadcastContent,
  prefix: string,
  index: number,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  /** 日本語 (既定フィールド) 1 件を検査する。 */
  const checkJapanese = (
    value: unknown,
    label: string,
    target: Omit<ValidationTarget, 'contentIndex'>,
  ): void => {
    if (typeof value !== 'string' || isWellFormedSsml(value)) return;
    push(
      `${prefix}: ${label}の SSML が正しくありません` +
        `（<speak> で始まり </speak> で終わる必要があります。</speak> の外に文字が残っていませんか）`,
      target,
    );
  };

  /** 言語別文面 (`*_i18n`) を言語ごとに検査する。 */
  const checkLocalized = (
    localized: LocalizedText | undefined,
    label: string,
    testIdBase: string,
    target: Omit<ValidationTarget, 'contentIndex' | 'testId'> = {},
  ): void => {
    if (!localized || typeof localized !== 'object') return;
    for (const [language, text] of Object.entries(localized)) {
      if (typeof text !== 'string' || isWellFormedSsml(text)) continue;
      const languageLabel = findPollyLanguage(language)?.label ?? language;
      push(
        `${prefix}: ${label}の${languageLabel}の SSML が正しくありません` +
          `（<speak> で始まり </speak> で終わる必要があります。</speak> の外に文字が残っていませんか）`,
        // 該当言語の入力欄を直接指し示す (赤枠 + スクロール)。
        { ...target, testId: localizedTextFieldTestId(testIdBase, language) },
      );
    }
  };

  /** 遅延放送パターン行 (自列車 / 接続線 / 乗換駅別) をまとめて検査する。 */
  const checkPatterns = (
    patterns: DelayBroadcastPattern[] | undefined,
    label: string,
    testIdPrefix: string,
    extra: Omit<ValidationTarget, 'contentIndex' | 'testId' | 'patternIndex'> = {},
  ): void => {
    if (!Array.isArray(patterns)) return;
    patterns.forEach((pattern, i) => {
      const rowLabel = `${label}パターン${i + 1}`;
      const rowTestId = `${testIdPrefix}-${i}`;
      checkJapanese(pattern?.ssml_template, rowLabel, {
        ...extra,
        patternIndex: i,
        testId: rowTestId,
      });
      checkLocalized(pattern?.ssml_template_i18n, rowLabel, rowTestId, {
        ...extra,
        patternIndex: i,
      });
    });
  };

  if (content.category === 'free_text') {
    const props = content.properties as Partial<FreeTextProperties>;
    checkJapanese(props.ssml, '任意テキストの SSML', { testId: 'content-free-text-ssml' });
    checkLocalized(props.ssml_i18n, '任意テキストの SSML', `free-text-${index}`);
    return;
  }
  if (content.category === 'self_train_delay') {
    const props = content.properties as Partial<SelfTrainDelayProperties>;
    checkPatterns(props.delay_patterns, '自列車遅延 ', 'self-train-delay-ssml-template');
    return;
  }
  if (content.category === 'connecting_line_delay') {
    const props = content.properties as Partial<ConnectingLineDelayProperties>;
    checkJapanese(props.ssml_template, '接続線遅延の SSML テンプレート', {
      testId: 'content-ssml-template',
    });
    checkLocalized(
      props.ssml_template_i18n,
      '接続線遅延の SSML テンプレート',
      `connecting-line-delay-${index}`,
    );
    checkPatterns(props.delay_patterns, '接続線遅延 ', 'connecting-line-delay-ssml-template');
    return;
  }
  if (content.category === 'connecting_line_delay_by_station') {
    const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
    checkJapanese(props.ssml_intro_template, '乗換駅別の冒頭 SSML', {
      testId: 'connecting-station-intro-template',
    });
    checkLocalized(
      props.ssml_intro_template_i18n,
      '乗換駅別の冒頭 SSML',
      'connecting-station-intro',
    );
    // 上限超過時の案内文 (日本語 / 言語別)。固定文を使う動作 (固定文のみ / 併用) のときだけ
    // 放送に使われるが、壊れた SSML を保存させないため、値があれば常に検査する。
    checkJapanese(props.over_limit_ssml_template, '乗換駅別の上限超過時の案内文', {
      testId: 'connecting-station-over-limit-template',
    });
    checkLocalized(
      props.over_limit_ssml_template_i18n,
      '乗換駅別の上限超過時の案内文',
      'connecting-station-over-limit',
    );
    if (!Array.isArray(props.lines)) return;
    props.lines.forEach((line, li) => {
      const lineLabel = `接続線${li + 1}の SSML`;
      checkJapanese(line?.ssml_line_template, lineLabel, {
        lineIndex: li,
        testId: `connecting-line-block-template-${li}`,
      });
      checkLocalized(line?.ssml_line_template_i18n, lineLabel, `connecting-line-block-${li}`, {
        lineIndex: li,
      });
      checkPatterns(
        line?.delay_patterns,
        `接続線${li + 1}の `,
        `connecting-station-line-${li}-direction-ssml`,
        { lineIndex: li },
      );
    });
  }
}

/**
 * 共有テキスト (`input_mode === 'synced'`) の入力エラーを集める。
 *
 * - 参照先 (`synced_ref`) が未選択ならエラー。
 * - 共有テキストは日本語専用: 放送言語が `['ja']` 以外 (外国語を含む) ならエラー。
 *   `languages` 未設定は「日本語のみ」(既存データの後方互換) なので許可する。
 * - 放送条件 (`conditions`) は通常テキストと同じ規則で検査する。
 *
 * 参照先がマスタに実在するかはサーバが検査する (画面側では一覧の取得失敗と区別できないため検査しない)。
 */
function collectSyncedTextIssues(
  content: BroadcastContent,
  props: Partial<FreeTextProperties>,
  prefix: string,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  const ref = props.synced_ref;
  const hasRef =
    !!ref &&
    typeof ref.route_id === 'string' &&
    ref.route_id.trim() !== '' &&
    typeof ref.template_id === 'string' &&
    ref.template_id.trim() !== '';
  if (!hasRef) {
    push(`${prefix} (任意テキスト): 配布テンプレートを選択してください`, {
      testId: 'content-free-text-template-open',
    });
  }
  const languages = Array.isArray(content.languages) ? content.languages : [];
  if (languages.some((language) => language !== DEFAULT_LANGUAGE_KEY)) {
    push(
      `${prefix} (任意テキスト): 配布テンプレートは日本語専用です` +
        `（放送言語を「日本語のみ」にするか、入力方法を変えてください）`,
      { testId: 'content-free-text-template-open' },
    );
  }
  if (Array.isArray(props.conditions) && props.conditions.length > 0) {
    for (let ci = 0; ci < props.conditions.length; ci++) {
      const condErr = validateBroadcastCondition(
        props.conditions[ci] as Partial<BroadcastCondition> | undefined,
        `${prefix} (任意テキスト) 条件${ci + 1}`,
      );
      if (condErr) {
        push(condErr, { conditionIndex: ci, testId: `broadcast-condition-row-${ci}` });
      }
    }
  }
}

/**
 * 任意テキスト放送 (free_text) の **言語別文面の入力漏れ** を収集する (Issue #545)。
 *
 * ## なぜ必須にするのか
 *
 * 放送側は当該言語の文面 (`text_i18n[lang]` / `ssml_i18n[lang]` /
 * `audio_key_i18n[lang]`) が無いと日本語 (`text` / `ssml` / `audio_key`) へ
 * フォールバックする。そのため未入力のまま保存すると
 *
 *   「英語を選択したのに、英語の放送時間に日本語が流れる」
 *
 * という **エラーにならず静かに間違った案内をする** 状態になる。
 * 画面上も「入力欄が空なだけ」で異常が分からないため、保存前に弾く。
 *
 * ## 遅延放送と扱いが違う理由
 *
 * 自列車遅延 / 接続線遅延の SSML は「空欄 = 定型文マスタへ委譲」(Issue #527)
 * が正しい保存規約であり、必須にするとマスタ編集の一括反映が効かなくなる。
 * 任意テキストは定型文マスタを持たない (文面の出どころが他に無い) ため、
 * **任意テキストに限って** 必須チェックを行う。
 *
 * ## 入力方法 (input_mode) ごとに見る欄を変える
 *
 * 画面に出ていない欄を必須にすると「見えない項目のせいで保存できない」状態に
 * なるため、`resolveInputMode()` と同じ規則で判定した現在の入力方法に対応する
 * `*_i18n` のみを検査する (音声ファイルモードは言語別音源が任意運用なので、
 * 日本語音源へのフォールバックを許容して検査対象にしない)。
 *
 * @param content 検証対象のコンテンツ (`languages` を読む)
 * @param props `content.properties` (任意テキストのプロパティ)
 * @param prefix エラーメッセージの接頭辞 (例: 「コンテンツ #1」)
 * @param index `contents[]` の 0 始まり index (testId の組み立てに使う)
 * @param push issue を積むコールバック
 */
function collectFreeTextLocalizedIssues(
  content: BroadcastContent,
  props: Partial<FreeTextProperties>,
  prefix: string,
  index: number,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  // `languages` 未設定は「日本語のみ」(既存データの後方互換) なので検査不要。
  const languages = Array.isArray(content.languages) ? content.languages : [];
  const targets = languages.filter((language) => language !== DEFAULT_LANGUAGE_KEY);
  if (targets.length === 0) return;

  /*
   * 検査対象は「画面に出ている入力欄」だけ。
   *
   * - text : 通常テキストモード → `text_i18n`
   * - ssml : SSML モード       → `ssml_i18n`
   * - audio: 音声ファイルモード → 検査しない (言語別音源は任意運用のため)
   */
  const mode = resolveFreeTextInputMode(props);
  if (mode === 'audio') return;
  const localized = mode === 'ssml' ? props.ssml_i18n : props.text_i18n;

  for (const language of targets) {
    const text = localized?.[language];
    // SSML モードでは中身のない `<speak></speak>` も「未入力」として扱う
    // (放送すると無音になるため。バックエンド `_is_empty_ssml` と同じ規則)。
    const filled =
      typeof text === 'string' && text.trim() !== '' && !(mode === 'ssml' && isEmptySsml(text));
    if (filled) continue;
    // 言語名は Polly マスタ (単一の正源) から引く。ここでラベルを二重管理すると
    // マスタの表示名と画面上の見出しが食い違う。
    const languageLabel = findPollyLanguage(language)?.label ?? language;
    push(
      `${prefix} (任意テキスト): ${languageLabel}の文面を入力してください` +
        `（未入力のままだと日本語が放送されます）`,
      // 該当言語の入力欄を直接指し示す (赤枠 + スクロール)。
      // testId 規則は `LocalizedTextFields` と共有する
      // (`FreeTextFields` が `translateTestId={`free-text-${contentIndex}`}` を渡す)。
      { testId: localizedTextFieldTestId(`free-text-${index}`, language) },
    );
  }
}

/**
 * 任意テキスト放送 (free_text) の **言語別文面の文字数超過** を収集する。
 *
 * 日本語の通常テキスト ({@link MAX_FREE_TEXT_LENGTH}) と **同じ見せ方** にする:
 * 日本語のメッセージ + 現在の文字数 + 該当言語の入力欄を指す testId (赤枠・スクロール)。
 * 以前は外国語だけ画面で検査されず、サーバの英語メッセージ
 * (`contents[0].properties.text_i18n.en must be 3000 characters or fewer (got N)`)
 * が出ていた。
 *
 * ## 検査する範囲
 *
 * サーバ (`_normalize_i18n_text_map`) は入力方法 (`input_mode`) に関係なく
 * `text_i18n` / `ssml_i18n` の **すべての言語** を検査する。画面だけ範囲を狭めると
 * 「画面では保存できるのに API が 400」になるため、同じ範囲で検査する。
 *
 * @param props `content.properties` (任意テキストのプロパティ)
 * @param prefix エラーメッセージの接頭辞 (例: 「コンテンツ #1」)
 * @param index `contents[]` の 0 始まり index (testId の組み立てに使う)
 * @param push issue を積むコールバック
 */
function collectFreeTextLocalizedLengthIssues(
  props: Partial<FreeTextProperties>,
  prefix: string,
  index: number,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  const fields: Array<{ localized: LocalizedText | undefined; kind: string }> = [
    { localized: props.text_i18n, kind: '通常テキスト' },
    { localized: props.ssml_i18n, kind: 'SSML' },
  ];
  for (const { localized, kind } of fields) {
    if (!localized || typeof localized !== 'object') continue;
    for (const [language, text] of Object.entries(localized)) {
      if (typeof text !== 'string') continue;
      const length = countTextCharacters(text);
      if (length <= MAX_I18N_TEXT_LENGTH) continue;
      // 言語名は Polly マスタ (単一の正源) から引く (入力欄の見出しと食い違わせない)。
      const languageLabel = findPollyLanguage(language)?.label ?? language;
      push(
        `${prefix} (任意テキスト): ${languageLabel}の放送文面 (${kind}) は${MAX_I18N_TEXT_LENGTH}文字以内で入力してください` +
          `（現在 ${length} 文字。超過すると放送できません）`,
        // 該当言語の入力欄を直接指し示す (赤枠 + スクロール)。
        { testId: localizedTextFieldTestId(`free-text-${index}`, language) },
      );
    }
  }
}

/**
 * 線区愛称の **言語別名称の入力漏れ** を収集する (Issue #585)。
 *
 * ## なぜ必須にするのか
 *
 * 放送側 (`lambda/common/ssml_builder.py` の
 * `resolve_route_nickname_for_language`) は当該言語の愛称が無いと
 *
 *   `route_nickname_i18n[lang]` → `route_nickname_i18n['ja']` → `route_nickname`
 *
 * の順にフォールバックする。そのため **日本語の愛称だけを入れて保存** すると、
 * `{route_name}` / `{route}` プレースホルダへ日本語がそのまま埋め込まれた
 * 英語・中国語・韓国語の文面が生成される。
 *
 *   en: `Your connecting 横浜線 is currently about 10 minutes late.`
 *   ko: `환승하시는 横浜線은 현재 약 10분 지연되고 있습니다.`
 *
 * さらに Polly は `language` に対応する外国語ボイス (Joanna / Zhiyu / Seoyeon)
 * しか使わないため、**混入した日本語は必ず外国語ボイスが読み上げる**。
 * 漢字・カタカナを英語の発音規則で処理するので線名が判別不能な音声になる。
 *
 * 文面 (`text_i18n` / `ssml_i18n`) には Issue #545 で同じ検査があるのに、
 * 愛称 (固有名詞) だけが漏れていた。その **非対称性** を解消する。
 *
 * ## 日本語の愛称が未入力なら検査しない
 *
 * 愛称そのものが未設定なら放送側は `route_name` (Transformer routeName) →
 * `route_name_fallback(route_id, language)` (Issue #583 で言語別の接尾辞) へ
 * 落ちるため日本語は混入しない。「愛称を使わない」運用を壊さないよう、
 * **日本語の愛称が入っている場合に限って** 外国語の名称を必須とする。
 *
 * ⚠️ サーバ側 (`_validate_route_nickname_localized`) と **同じ規則** にすること。
 * 判定がズレると「画面では保存できるのに API が 400」(またはその逆) になる。
 *
 * @param source `route_nickname` / `route_nickname_i18n` を持ち得るオブジェクト
 * @param languages 対象の放送言語 (`content.languages` / 地点の和集合)
 * @param label 入力欄の見出し (例: 「接続線 線区愛称」)
 * @param testIdPrefix `LocalizedRouteNicknameFields` へ渡している `testIdPrefix`
 * @param prefix エラーメッセージの接頭辞 (例: 「コンテンツ #1 (接続線遅延)」)
 * @param push issue を積むコールバック
 * @param extra 追加の位置情報 (`lineIndex` 等)
 */
export function collectRouteNicknameLocalizedIssues(
  source: { route_nickname?: unknown; route_nickname_i18n?: LocalizedText } | undefined,
  languages: readonly string[],
  label: string,
  testIdPrefix: string,
  prefix: string,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
  extra: Omit<ValidationTarget, 'contentIndex' | 'testId'> = {},
): void {
  if (!source || typeof source !== 'object') return;
  const targets = languages.filter((language) => language !== DEFAULT_LANGUAGE_KEY);
  if (targets.length === 0) return;

  const localized = source.route_nickname_i18n;
  // 放送側のフォールバック順 (i18n['ja'] → route_nickname) と同じ規則で
  // 「日本語の愛称が入っているか」を判定する。
  const japaneseFromI18n = localized?.[DEFAULT_LANGUAGE_KEY];
  const japanese =
    typeof japaneseFromI18n === 'string' && japaneseFromI18n.trim() !== ''
      ? japaneseFromI18n
      : source.route_nickname;
  // 愛称そのものが未設定なら線名は route_name / route_id へ落ちるので検査不要。
  if (typeof japanese !== 'string' || japanese.trim() === '') return;

  for (const language of targets) {
    const nickname = localized?.[language];
    if (typeof nickname === 'string' && nickname.trim() !== '') continue;
    // 言語名は Polly マスタ (単一の正源) から引く。ここでラベルを二重管理すると
    // マスタの表示名と画面上の見出しが食い違う。
    const languageLabel = findPollyLanguage(language)?.label ?? language;
    push(
      `${prefix}: ${languageLabel}の${label}を入力してください` +
        `（未入力のままだと日本語が放送されます）`,
      // 該当言語の入力欄を直接指し示す (赤枠 + スクロール)。
      // testId 規則は `LocalizedRouteNicknameFields` と共有する。
      { ...extra, testId: localizedTextFieldTestId(testIdPrefix, language) },
    );
  }
}

/**
 * 接続線の線区愛称の **必須チェック** (日本語 + 放送言語すべて)。
 *
 * 接続線の愛称は、放送 SSML の `{route_name}` / `{route}` に最優先で使われる。
 * 空のままだと運行データ (Transformer) の線名に依存し、画面から実際に流れる線名が
 * 分からない。そこで線区を選ぶとマスタの名称が自動入力される (`routeNicknameDefaults.ts`)
 * 前提で、空欄の保存を画面で止める。
 *
 * - 日本語 (`route_nickname` / `route_nickname_i18n.ja`) は常に必須。
 * - 日本語以外の放送言語は、その言語の名称を必須とする。
 *   日本語の愛称で代用すると外国語ボイスが日本語を読んでしまうため
 *   (`collectRouteNicknameLocalizedIssues` と同じ理由)。
 *
 * ⚠️ 画面だけの検査で、API (`_validate_route_nickname_localized`) は必須にしない。
 *    愛称が空で保存済みの既存データを、編集時に 400 にしないため。
 *
 * @param source `route_nickname` / `route_nickname_i18n` を持ち得るオブジェクト
 * @param languages 対象の放送言語
 * @param japaneseTestId 日本語の愛称欄の `data-testid`
 * @param localizedTestIdPrefix 言語別欄 (`LocalizedRouteNicknameFields`) の `testIdPrefix`
 * @param prefix エラーメッセージの接頭辞
 * @param push issue を積むコールバック
 * @param extra 追加の位置情報 (`lineIndex` 等)
 */
export function collectRequiredRouteNicknameIssues(
  source: { route_nickname?: unknown; route_nickname_i18n?: LocalizedText } | undefined,
  languages: readonly string[],
  japaneseTestId: string,
  localizedTestIdPrefix: string,
  prefix: string,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
  extra: Omit<ValidationTarget, 'contentIndex' | 'testId'> = {},
): void {
  const localized = source?.route_nickname_i18n;
  const japaneseFromI18n = localized?.[DEFAULT_LANGUAGE_KEY];
  const japanese =
    typeof japaneseFromI18n === 'string' && japaneseFromI18n.trim() !== ''
      ? japaneseFromI18n
      : source?.route_nickname;
  if (typeof japanese !== 'string' || japanese.trim() === '') {
    push(`${prefix}: 線区愛称を入力してください`, { ...extra, testId: japaneseTestId });
  }

  for (const language of languages.filter((l) => l !== DEFAULT_LANGUAGE_KEY)) {
    const nickname = localized?.[language];
    if (typeof nickname === 'string' && nickname.trim() !== '') continue;
    const languageLabel = findPollyLanguage(language)?.label ?? language;
    push(
      `${prefix}: ${languageLabel}の線区愛称を入力してください` +
        `（未入力のままだと日本語が放送されます）`,
      { ...extra, testId: localizedTextFieldTestId(localizedTestIdPrefix, language) },
    );
  }
}

/**
 * 接続線ブロックの「遅延検索対象の線区」(`route_ids`) をバリデーションする。
 *
 * サーバ側 `_normalize_connecting_route_ids`
 * (`lambda/control-panel-api/index.py`) が 400 を返す条件をそのまま画面側でも
 * 検査する。UI (`ConnectingRouteIdsSelect`) は選択済みを追加候補から除外し、
 * 上限に達したらプルダウンを disabled にするので通常操作では発生しないが、
 * **JSON インポート経由の値** はここを通らないため保存前に弾く必要がある。
 *
 * - 未設定 / 空配列は正常 (`route_id` 単数へフォールバック = 後方互換)。
 * - 配列でない → エラー
 * - 要素が文字列でない / 空文字 → エラー
 * - 重複 → エラー
 * - `MAX_CONNECTING_ROUTE_IDS` 超過 → エラー
 */
function collectConnectingRouteIdsIssues(
  routeIds: unknown,
  linePrefix: string,
  lineIndex: number,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  if (routeIds === undefined || routeIds === null) return;
  const target = {
    lineIndex,
    testId: `connecting-line-route-ids-${lineIndex}`,
  };
  if (!Array.isArray(routeIds)) {
    push(`${linePrefix}: 遅延検索対象の線区の設定が不正です`, target);
    return;
  }
  // 空配列は「未設定」と同義 (UI 側で undefined に落とすが、既存データ /
  // インポートでは空配列が入り得る)。サーバ側もキーごと省略するので正常扱い。
  if (routeIds.length === 0) return;
  if (routeIds.length > MAX_CONNECTING_ROUTE_IDS) {
    push(`${linePrefix}: 遅延検索対象の線区は最大 ${MAX_CONNECTING_ROUTE_IDS} 件までです`, target);
  }
  const seen = new Set<string>();
  for (const routeId of routeIds) {
    if (typeof routeId !== 'string' || routeId.trim() === '') {
      push(`${linePrefix}: 遅延検索対象の線区に空の値が含まれています`, target);
      continue;
    }
    const trimmed = routeId.trim();
    if (seen.has(trimmed)) {
      // 同じ線区を二重に設定しても情報が増えず、案内が二重になりかねないため弾く。
      push(`${linePrefix}: 遅延検索対象の線区が重複しています (${trimmed})`, target);
      continue;
    }
    seen.add(trimmed);
  }
}

/**
 * 方面ブロックの「線区ごとの判定方面」(`route_directions`) をバリデーションする。
 *
 * サーバ側 `_normalize_direction_overrides` (`lambda/control-panel-api/index.py`) が
 * 400 を返す条件をそのまま画面側でも検査する。UI (`RouteDirectionOverridesTable`) は
 * マスタの方面しか選べないため通常操作では発生しないが、**JSON インポート経由の値**
 * はここを通らないため保存前に弾く必要がある。
 *
 * - 未設定 / 空オブジェクトは正常 (旧データの方面ブロック共通 `direction_id` へフォールバック)。
 * - オブジェクトでない (配列 / プリミティブ) → エラー
 * - キーが空文字 → エラー
 * - 値が `"0"` / `"1"` 以外 → エラー
 *   (`/route-stops` / `/stop-times` API の `direction_id` クエリが 0/1 のみ許容する)
 */
function collectRouteDirectionsIssues(
  routeDirections: unknown,
  dirPrefix: string,
  lineIndex: number,
  directionIndex: number,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  if (routeDirections === undefined || routeDirections === null) return;
  const target = {
    lineIndex,
    directionIndex,
    testId: `connecting-direction-route-directions-${lineIndex}-${directionIndex}`,
  };
  if (typeof routeDirections !== 'object' || Array.isArray(routeDirections)) {
    push(`${dirPrefix}: 線区ごとの判定方面の設定が不正です`, target);
    return;
  }
  for (const [routeId, directionId] of Object.entries(routeDirections as Record<string, unknown>)) {
    if (routeId.trim() === '') {
      push(`${dirPrefix}: 線区ごとの判定方面に空の線区が含まれています`, target);
      continue;
    }
    // 空文字は UI 側で「(指定なし)」= キー削除に落とすが、既存データ /
    // インポートでは残り得るので「未設定と同義」として正常扱いにする。
    if (typeof directionId === 'string' && directionId.trim() === '') continue;
    if (directionId !== '0' && directionId !== '1') {
      push(
        `${dirPrefix}: 線区ごとの判定方面 (${routeId}) は 0 または 1 で指定してください`,
        target,
      );
    }
  }
}

/**
 * 運行情報 (`train_info`) をバリデーションする。
 *
 * 運行情報のみのカテゴリは乗換駅・遅延判定・各 SSML テンプレートを一切使わず、接続線ごとに
 * 選択したアイステイションズ路線の運行情報のみを放送する。そのため「放送内容が成立するための
 * 最低条件」だけを確認する。
 *
 * - `lines` が 1 件以上あること (接続線ブロックが放送単位なので必須)
 * - 各接続線に **アイステイションズ路線が 1 件以上** 紐付いていること
 *   (参照元が無いと運行情報が一切取得できず、放送内容が空になる)
 *
 * ControlPanelApi (`_normalize_train_info_properties`) も同じ条件で 400 を返す。
 */
function collectTrainInfoIssues(
  props: Partial<TrainInfoProperties>,
  prefix: string,
  push: (message: string, target?: Omit<ValidationTarget, 'contentIndex'>) => void,
): void {
  if (!Array.isArray(props.lines) || props.lines.length === 0) {
    push(`${prefix} (運行情報): 運行情報と連携する路線を 1 件以上選択してください`, {
      testId: 'train-info-line-istations-lines-0-add-select',
    });
    return;
  }
  for (let li = 0; li < props.lines.length; li++) {
    const line = props.lines[li] as Partial<TrainInfoLineEntry> | undefined;
    const linePrefix = `${prefix} (運行情報) 接続線${li + 1}`;
    if (!line || typeof line !== 'object') {
      push(`${linePrefix}: 接続線の設定が不正です`, { lineIndex: li });
      continue;
    }
    if (!Array.isArray(line.istations_lines) || line.istations_lines.length === 0) {
      push(`${linePrefix}: アイステイションズ路線を 1 件以上選択してください`, {
        lineIndex: li,
        testId: `train-info-line-istations-lines-${li}-add-select`,
      });
    }
  }
}

/**
 * 任意テキスト放送 (free_text) の放送条件 1 件をバリデーションする。

 *
 * `condition_type` (判別可能ユニオンの discriminant) で検証内容を振り分ける。
 *
 * - `arrival_track`: 停車番線 (GTFS 子 stop_id) の指定を検証する。
 * - `connecting_line_delay` / **未指定**: 従来どおり接続線遅延の条件として検証する
 *   (未指定を既存種別として扱うため、既存データの検証結果は一切変わらない)。
 *
 * @param cond 条件オブジェクト (undefined の場合はエラー扱い)
 * @param prefix エラーメッセージの接頭辞 (例: 「コンテンツ #1 (任意テキスト) 条件1」)
 * @returns バリデーションエラー文字列 (問題なしなら null)
 */
export function validateBroadcastCondition(
  cond: Partial<BroadcastCondition> | undefined,
  prefix: string,
): string | null {
  if (!cond || typeof cond !== 'object') {
    return `${prefix}: 条件の設定が不正です`;
  }
  const conditionType = (cond as Partial<ArrivalTrackCondition>).condition_type;
  if (conditionType === 'arrival_track') {
    return validateArrivalTrackCondition(cond as Partial<ArrivalTrackCondition>, prefix);
  }
  if (conditionType !== undefined && conditionType !== 'connecting_line_delay') {
    return `${prefix}: 未対応の条件種別 ${String(conditionType)}`;
  }
  return validateConnectingLineDelayCondition(
    cond as Partial<ConnectingLineDelayCondition>,
    prefix,
  );
}

/**
 * 停車番線条件 (`condition_type: 'arrival_track'`) をバリデーションする。
 *
 * - `arrival_stop_id`: GTFS 子 stop_id (例: `"南浦和:上中"`) を必須とする。
 *   駅と番線を 1 フィールドで表すため、駅・番線の整合チェックは不要。
 *
 * 廃止済みの `arrival_track_match` (`eq` / `ne`) は **検証しない**。既存レコードに
 * 残っていてもエラーにせず保存できるようにする (保存 API 側も透過保存する)。
 * 関数名は据え置き (識別子 `arrival_track` を改名しない方針に合わせる)。
 */
function validateArrivalTrackCondition(
  cond: Partial<ArrivalTrackCondition>,
  prefix: string,
): string | null {
  if (typeof cond.arrival_stop_id !== 'string' || cond.arrival_stop_id.trim() === '') {
    return `${prefix}: 停車番線 (stop_id) を指定してください`;
  }
  return null;
}

/**
 * 接続線遅延条件 (`condition_type` 未指定 / `'connecting_line_delay'`) をバリデーションする。
 *
 * 接続線遅延コンテンツと同じ 3 モード (whole_line / section_scheduled / transfer_aware) を
 * サポートし、各モードの必須項目 (route_id / 区間駅 / 乗換駅・乗換時間) を確認する。
 *
 * `delay_minutes` (比較対象の遅延分数) は 0 以上の整数、`operator` は 'gte' / 'lte' のみ許可。
 *
 * ※ 判別可能ユニオン化に伴う切り出しであり、検証ロジック自体は従来から変更していない。
 */
function validateConnectingLineDelayCondition(
  cond: Partial<ConnectingLineDelayCondition>,
  prefix: string,
): string | null {
  if (typeof cond.route_id !== 'string' || cond.route_id.trim() === '') {
    return `${prefix}: 接続線 (route_id) を指定してください`;
  }
  if (cond.operator !== 'gte' && cond.operator !== 'lte') {
    return `${prefix}: 比較演算子 (以上 / 以下) を指定してください`;
  }
  if (
    typeof cond.delay_minutes !== 'number' ||
    Number.isNaN(cond.delay_minutes) ||
    cond.delay_minutes < 0 ||
    !Number.isInteger(cond.delay_minutes)
  ) {
    return `${prefix}: 遅延分数を 0 以上の整数で指定してください`;
  }
  const mode = cond.delay_judgement_mode ?? 'whole_line';
  if (mode === 'section_scheduled') {
    const hasStart = typeof cond.start_stop_id === 'string' && cond.start_stop_id.trim() !== '';
    const hasEnd = typeof cond.end_stop_id === 'string' && cond.end_stop_id.trim() !== '';
    if (!hasStart || !hasEnd) {
      return `${prefix}: 「折り返し列車の遅延（判定区間指定）」モードでは遅延判定開始駅と終了駅を指定してください`;
    }
  }
  if (mode === 'transfer_aware') {
    const hasTransferStop =
      typeof cond.transfer_stop_id === 'string' && cond.transfer_stop_id.trim() !== '';
    if (!hasTransferStop) {
      return `${prefix}: 「乗り換え列車の遅延」モードでは乗換駅を指定してください`;
    }
    if (
      typeof cond.transfer_time_seconds !== 'number' ||
      Number.isNaN(cond.transfer_time_seconds) ||
      cond.transfer_time_seconds < 0 ||
      !Number.isInteger(cond.transfer_time_seconds)
    ) {
      return `${prefix}: 「乗り換え列車の遅延」モードでは乗換時間 (秒) を 0 以上の整数で指定してください`;
    }
  }
  return null;
}

/**
 * 任意テキスト放送 (free_text) の放送条件の既定値を生成する。
 * 「＋条件を追加」ボタンで新規条件を追加する際の初期値として使う。
 *
 * デフォルトは「乗り換え列車の遅延で 5 分以上 (乗換時間 180 秒)」とし、
 * ユーザは route_id / 乗換駅を必ず選び直す。
 */
export function createDefaultBroadcastCondition(): ConnectingLineDelayCondition {
  return {
    // 既定は従来種別 (接続線遅延)。未指定でも同義だが、判別可能ユニオン化後は
    // 明示しておくことで種別セレクト (後続 SBI) の初期表示が一意になる。
    condition_type: 'connecting_line_delay',
    route_id: '',
    delay_judgement_mode: 'transfer_aware',
    transfer_time_seconds: 180,
    delay_minutes: 5,
    operator: 'gte',
  };
}

/** 配列内の要素を index 単位で並び替えるヘルパー (immutable) */
export function moveContent(
  contents: BroadcastContent[],
  fromIndex: number,
  toIndex: number,
): BroadcastContent[] {
  if (fromIndex === toIndex) return contents;
  if (fromIndex < 0 || fromIndex >= contents.length) return contents;
  if (toIndex < 0 || toIndex >= contents.length) return contents;
  const next = contents.slice();
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return contents;
  next.splice(toIndex, 0, moved);
  return next;
}
